import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FACEBOOK_PAGE_PERMISSIONS } from "../config/facebook";
import {
  Shield,
  Globe,
  FileText,
  ChevronDown,
  Search,
  // Clock, // Commented out - no longer used after hiding Avg Response Time metric
  AlertTriangle,
  CheckCircle,
  Calendar,
  MessageSquare,
  Zap,
  Loader2,
  RefreshCw,
  // Eye, // Commented out - using EyeOff instead
  EyeOff,
  Trash2,
  Plus,
  X,
  Save,
  TestTube,
  Edit3,
  Mail,
  ArrowLeft,
  Ban,
  Angry,
  Megaphone,
  Smile,
  Image,
  Link,
  Type,
  Wand2,
  HelpCircle,
  Frown,
  CornerDownRight,
  Filter,
  Check,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../components/ui/dropdown-menu";

import { useApi, API_ENDPOINTS } from "../lib/api";
import { usePostFilter } from "../contexts/PostFilterContext";
import { CommentAvatar, getCommenterIdentity } from "../lib/commenterIdentity";
import { joinPageRoom, leavePageRoom, onNewComment, offNewComment, onCommentUpdated, offCommentUpdated } from "../lib/socket";
import { ImpactStrip, ImpactCardKey } from "../components/ImpactStrip";
import {
  useModerationRunner,
  RunItemResult,
} from "../components/ModerationToast";
import { useReviewCount } from "../hooks/useReviewCount";
import ReactSelect from "react-select";
import commentsIllustration from "../assets/comments-illustration.png";

// Enhanced interfaces based on FacebookCommentModerationPage
interface ModerationRule {
  id: string;
  name?: string;
  scope: "global" | "post";
  postId?: string;
  trigger: {
    type: "contains" | "starts_with" | "ends_with" | "template" | "ai_intent";
    value: string;
    template?: string;
    /** Only for type "ai_intent": the user's own description of what to detect. */
    aiIntent?: {
      description: string;
      examples?: string[];
      counterExamples?: string[];
    };
  };
  actions: {
    type: "delete" | "hide" | "reply" | "dm" | "approve";
    autoReply?: string;
    messageType?: "static" | "ai"; // NEW: Message type for reply/dm actions
  }[];
  language: "derja" | "derja_latin" | "french" | "arabic" | "auto";
  useAI?: boolean; // Optional: Auto-determined by backend based on trigger type
  isActive: boolean;
  createdAt: string;
}

interface FacebookPost {
  id: string;
  message: string;
  created_time: string;
  picture?: string;
  permalink_url: string;
  attachments?: {
    data?: Array<{
      media?: {
        image?: {
          src?: string;
        };
        source?: string;
      };
      target?: {
        id?: string;
      };
    }>;
  };
}

interface FacebookPageData {
  id: string;
  name: string;
  picture?: string;
  prompt?: string;
  language?: string;
  platform?: "facebook" | "instagram";
  username?: string;
}

interface Comment {
  id: string;
  message: string;
  attachment?: {
    type?: string | null;
    url?: string | null;
    imageSrc?: string | null;
    title?: string | null;
  } | null;
  from: {
    name: string;
    id: string;
    picture?: string; // User profile picture URL
  };
  created_time: string;
  status: "approved" | "pending" | "hidden" | "deleted" | "error";
  postId?: string;
  postLink?: string;
  actionTaken?: "delete" | "hide" | "approve";
  moderationResult?: {
    action: string;
    reason: string;
    confidence: number;
    isAI?: boolean;
    matchedRules?: string[]; // Added for rule matching
    ruleId?: string; // Added for rule matching
  };
  autoReply?: {
    message?: string;
    type?: "public" | "private";
    sentAt?: string;
    successful?: boolean;
  };
  // Real-time animation states
  isNew?: boolean; // Just arrived via webhook
  isProcessing?: boolean; // Moderation in progress
  processingAction?: "approve" | "hide" | "delete" | "reply" | "dm"; // which action is in flight (drives per-button spinner)
  animationState?: "entering" | "delete" | "hide" | "approved" | "removing";
  // Optimistic-update rollback metadata. Populated when an action is in flight
  // (previousStatus) or when the most recent action failed (lastFailedAction +
  // friends), so the row can revert and expose a retry control.
  previousStatus?: "approved" | "pending" | "hidden" | "deleted" | "error";
  lastFailedAction?: "approve" | "hide" | "delete" | "reply" | "dm";
  lastFailedMessage?: string;
  lastFailedMessageType?: "static" | "ai";
  lastError?: string;
}

interface Statistics {
  totalRules: number;
  activeRules: number;
  moderatedComments: number;
  moderationAccuracy: number;
  totalComments: number;
  pendingModeration: number;
  autoModerated: number;
  flaggedForReview: number;
  averageResponseTime: number;
}

interface NewRuleType {
  name: string;
  scope: "global" | "post";
  postId: string;
  triggerType: "contains" | "starts_with" | "ends_with" | "template" | "ai_intent";
  triggerValue: string;
  template: string;
  /** Only for triggerType "ai_intent": an intent the user described themselves. */
  aiIntentDescription: string;
  aiIntentExamples: string[];
  /** Look-alike comments that must NOT be flagged — kills false positives. */
  aiIntentCounterExamples: string[];
  actions: {
    type: "delete" | "hide" | "reply" | "dm" | "approve";
    autoReply?: string;
    messageType?: "static" | "ai"; // NEW: Message type for reply/dm actions
  }[];
  language: "derja" | "derja_latin" | "french" | "arabic" | "auto";
  useAI?: boolean; // Optional: Only used internally, not sent to backend
  isActive: boolean; // NEW: Rule activation status
}

/**
 * The intents shown up front when picking an AI condition. There are ~18 built-in
 * templates, most of them fine-grained variations of "customer is asking a
 * question", which made the picker a wall of near-identical tiles.
 *
 * Only these four are visible by default and they are all business intents —
 * the questions that cost you a sale when nobody answers them. Everything else,
 * profanity and spam included, is one click away under "Show all" so no existing
 * rule becomes unreachable.
 */
const FEATURED_TEMPLATES = [
  "price_inquiry",
  "availability_inquiry",
  "how_to_order",
  "delivery_availability",
];

/** Business intent first in every list — that is what the shortlist optimises for. */
const CATEGORY_ORDER = [
  "Business Intent",
  "Content Quality",
  "Engagement Type",
  "Other",
];

/**
 * Clickable openers for the custom-intent field. Tapping one fills the sentence
 * in — the fastest way to show what a good description sounds like. Short label,
 * full sentence, so the hint line stays on one row.
 */
const INTENT_STARTERS = [
  {
    label: "wholesale pricing",
    text: "the customer asks about wholesale or bulk pricing",
  },
  {
    label: "haggling over price",
    text: "the customer wants to negotiate the price",
  },
  {
    label: "shipping abroad",
    text: "the customer asks if we ship outside the country",
  },
];

interface ModerationDashboardProps {
  view?: "rules" | "comments";
}

// Single-select comment status filter (replaces the old 3 toggle buttons).
type StatusFilter =
  | "all"
  | "needs_review"
  | "cleared"
  | "hidden"
  | "deleted"
  | "failed";

// Dropdown options, in display order. Labels render as "Status: {label}".
const STATUS_FILTER_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "needs_review", label: "Needs review" },
  { value: "cleared", label: "Cleared" },
  { value: "hidden", label: "Hidden" },
  { value: "deleted", label: "Deleted" },
  { value: "failed", label: "Failed" },
];

// Map each filter to the Comment.moderationStatus values it selects.
// null = no restriction (show every status).
const STATUS_FILTER_TO_MOD: Record<StatusFilter, string[] | null> = {
  all: null,
  needs_review: ["pending"],
  cleared: ["approved"],
  hidden: ["hidden"],
  deleted: ["deleted"],
  failed: ["error"],
};

const statusFilterLabel = (value: StatusFilter): string =>
  STATUS_FILTER_OPTIONS.find((o) => o.value === value)?.label ?? "All";

// Compact relative timestamp ("now", "5m ago", "2h ago", "3d ago"); falls back
// to a short date past a week. Returns "" for missing/invalid input.
const timeAgo = (input?: string | number | Date | null): string => {
  if (!input) return "";
  const then = new Date(input).getTime();
  if (Number.isNaN(then)) return "";
  const diffSec = Math.round((Date.now() - then) / 1000);
  if (diffSec < 0) return "just now";
  if (diffSec < 45) return "now";
  if (diffSec < 90) return "1m ago";
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return new Date(then).toLocaleDateString();
};

// Full, locale-formatted timestamp for hover tooltips on relative times.
const fullTimestamp = (input?: string | number | Date | null): string => {
  if (!input) return "";
  const d = new Date(input);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
};

// Helper function for action messages with proper grammar
const getActionMessage = (action: string, type: 'success' | 'error' = 'success'): string => {
  const messages = {
    success: {
      hide: 'Comment hidden successfully',
      delete: 'Comment deleted successfully',
      approve: 'Comment approved successfully',
      reply: 'Reply sent successfully',
      dm: 'Direct message sent successfully'
    },
    error: {
      hide: 'Failed to hide comment',
      delete: 'Failed to delete comment',
      approve: 'Failed to approve comment',
      reply: 'Failed to send reply',
      dm: 'Failed to send direct message'
    }
  };
  
  return messages[type][action as keyof typeof messages.success] || 
         (type === 'success' ? `Action ${action} completed` : `Action ${action} failed`);
};

// Common Rules — hydrated from GET /api/common-rules at runtime so the canonical
// list lives in the backend (backend/config/defaultRules.js) and cannot drift.
interface QuickRule {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  priority: number;
  defaultAction: 'hide' | 'delete';
  trigger: {
    type: 'contains' | 'starts_with' | 'ends_with' | 'template';
    value: string;
    template?: string;
  };
  useAI: boolean;
}

const QUICK_RULE_ICONS: Record<string, React.ReactNode> = {
  'Bad Words': <Ban className="h-4 w-4 text-red-500" />,
  'Hate Speech': <Angry className="h-4 w-4 text-red-600" />,
  'Positive Critique': <Smile className="h-4 w-4 text-green-500" />,
  'Negative Critique': <Frown className="h-4 w-4 text-amber-600" />,
  'Spam Comments': <Megaphone className="h-4 w-4 text-orange-500" />,
  'Emoji-Only Comments': <Smile className="h-4 w-4 text-yellow-500" />,
  'Image or Sticker Comments': <Image className="h-4 w-4 text-purple-500" />,
  'External Links': <Link className="h-4 w-4 text-blue-500" />,
};

const ModerationDashboard: React.FC<ModerationDashboardProps> = ({ view = "comments" }) => {
  const { pageId } = useParams<{ pageId: string }>();
  const navigate = useNavigate();
  const reviewCount = useReviewCount(pageId);
  const api = useApi();
  const { setFilterState, resetFilterState } = usePostFilter();
  const currentPageIdRef = useRef<string | undefined>(pageId);
  const isInitialLoad = useRef(true);

  // Common Rules tile list — fetched from the backend so the canonical shape
  // lives in one place. Empty until the fetch resolves; render gracefully.
  const [quickRules, setQuickRules] = useState<QuickRule[]>([]);
  useEffect(() => {
    let cancelled = false;
    api.get('/api/common-rules')
      .then((res) => {
        if (cancelled) return;
        const list: QuickRule[] = (res.data || []).map((r: any, i: number) => ({
          id: `quick_${(r.name || '').toLowerCase().replace(/\s+/g, '_')}`,
          name: r.name,
          description: r.description || '',
          icon: QUICK_RULE_ICONS[r.name] || <Shield className="h-4 w-4 text-gray-500" />,
          priority: i + 1,
          defaultAction: r.defaultAction || 'hide',
          trigger: r.trigger,
          useAI: !!r.useAI,
        }));
        setQuickRules(list);
      })
      .catch((err) => {
        console.error('Failed to load common rules:', err);
      });
    return () => { cancelled = true; };
  }, [api]);

  // Debounced search functions
  const debouncedCommentSearch = useCallback(
    debounce((query: string) => {
      console.log("🔍 [SEARCH] Comment search triggered:", query);
      setCommentSearchQuery(query);
    }, 500),
    []
  );

  // Debounce utility function
  function debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number
  ): (...args: Parameters<T>) => void {
    let timeout: NodeJS.Timeout;
    return (...args: Parameters<T>) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  }

  // Helper function to get post image from either picture or attachments
  const getPostImage = (post: FacebookPost | null): string | null => {
    if (!post) return null;
    
    // Try direct picture field first
    if (post.picture) return post.picture;
    
    // Try attachments
    if (post.attachments?.data && post.attachments.data.length > 0) {
      const firstAttachment = post.attachments.data[0];
      
      // Try media.image.src
      if (firstAttachment.media?.image?.src) {
        return firstAttachment.media.image.src;
      }
      
      // Try media.source
      if (firstAttachment.media?.source) {
        return firstAttachment.media.source;
      }
    }
    
    return null;
  };

  // Utility function to check if a rule matches a comment
  const doesRuleMatchComment = (
    rule: ModerationRule,
    comment: Comment
  ): boolean => {
    if (!rule.isActive) {
      console.log(`🔍 [FRONTEND] Rule "${rule.name}" is not active`);
      return false;
    }

    const commentLower = comment.message.toLowerCase();
    const triggerLower = rule.trigger.value.toLowerCase();

    console.log(`🔍 [FRONTEND] Checking rule "${rule.name}":`);
    console.log(`   - Trigger type: ${rule.trigger.type}`);
    console.log(`   - Trigger value: "${rule.trigger.value}"`);
    console.log(`   - Comment: "${comment.message}"`);

    let matches = false;

    switch (rule.trigger.type) {
      case "contains":
        matches = commentLower.includes(triggerLower);
        console.log(`   - Contains match: ${matches}`);
        break;
      case "starts_with":
        matches = commentLower.startsWith(triggerLower);
        console.log(`   - Starts with match: ${matches}`);
        break;
      case "ends_with":
        matches = commentLower.endsWith(triggerLower);
        console.log(`   - Ends with match: ${matches}`);
        break;
      case "template":
      case "ai_intent":
        // AI-classified rules (built-in template or user-described intent) are
        // decided on the server; the client only reflects what it reported.
        matches = !!(
          rule.useAI &&
          comment.moderationResult?.isAI &&
          comment.moderationResult?.matchedRules?.includes(rule.id)
        );
        console.log(
          `   - AI match: ${matches} (type: ${rule.trigger.type}, useAI: ${rule.useAI}, isAI: ${comment.moderationResult?.isAI})`
        );
        break;
      default:
        console.log(`   - Unknown trigger type: ${rule.trigger.type}`);
        matches = false;
    }

    console.log(
      `🔍 [FRONTEND] Rule "${rule.name}" ${
        matches ? "MATCHES" : "does not match"
      } comment`
    );
    return matches;
  };

  // Utility function to get applicable rules for a comment (considering scope)
  const getApplicableRulesForComment = (
    comment: Comment,
    rulesToCheck: ModerationRule[] = rules
  ): ModerationRule[] => {
    return rulesToCheck.filter((rule) => {
      // Check if rule is active
      if (!rule.isActive) return false;

      // For post-specific rules, only apply if they match the comment's post
      if (rule.scope === "post" && rule.postId !== comment.postId) {
        return false;
      }

      // Check if rule matches the comment
      return doesRuleMatchComment(rule, comment);
    });
  };

  // Utility function to determine the most severe action from multiple rules
  const getMostSevereAction = (
    rules: ModerationRule[]
  ): { type: string; reason: string } => {
    const allActions = rules.flatMap((rule) => rule.actions);

    // Priority order: delete > hide > reply > dm > approve
    const mostSevereAction = allActions.find(
      (action) => action.type === "delete"
    ) ||
      allActions.find((action) => action.type === "hide") ||
      allActions.find((action) => action.type === "reply") ||
      allActions.find((action) => action.type === "dm") ||
      allActions.find((action) => action.type === "approve") || {
        type: "approve" as const,
      };

    const reason = `Applied ${rules.length} rules: ${rules
      .map((r) => r.name || r.trigger.value)
      .join(", ")}`;

    return { type: mostSevereAction.type, reason };
  };

  // Utility function to validate rule actions
  const validateRuleActions = (rule: ModerationRule): boolean => {
    if (!rule.actions || !Array.isArray(rule.actions)) {
      console.error(
        "❌ [FRONTEND] Invalid rule - missing actions array:",
        rule
      );
      return false;
    }

    for (const action of rule.actions) {
      if (
        !action.type ||
        !["approve", "hide", "delete", "reply", "dm"].includes(action.type)
      ) {
        console.error(
          "❌ [FRONTEND] Invalid action type in rule:",
          action.type
        );
        return false;
      }
    }

    return true;
  };

  // Enhanced state
  const [selectedSection, setSelectedSection] = useState<"global" | string>(
    "global"
  );
  const [rules, setRules] = useState<ModerationRule[]>([]);
  const [comments, setComments] = useState<Comment[]>([]);
  const [page, setPage] = useState<FacebookPageData | null>(null);
  // Platform-aware derived values — drive all FB-vs-IG copy/labels/actions off these.
  const isInstagram = page?.platform === "instagram";
  const platformName = isInstagram ? "Instagram" : "Facebook";

  // Non-blocking moderation progress toasts (bottom-right, stack upward).
  const { run: runModeration } = useModerationRunner();

  const [_statistics, setStatistics] = useState<Statistics>({
    totalRules: 0,
    activeRules: 0,
    moderatedComments: 0,
    moderationAccuracy: 0,
    totalComments: 0,
    pendingModeration: 0,
    autoModerated: 0,
    flaggedForReview: 0,
    averageResponseTime: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  // Timestamp of the last successful comments load — powers the "synced N ago"
  // line in the toolbar. Stored as epoch ms so timeAgo() can format it.
  const [lastLoadedAt, setLastLoadedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Comments table state
  const [commentSearchQuery, setCommentSearchQuery] = useState("");
  const [commentDateFilter, setCommentDateFilter] = useState("all");

  const [selectedRuleFilters, setSelectedRuleFilters] = useState<string[]>([]);
  const [selectedComments, setSelectedComments] = useState<string[]>([]);

  // Inline per-comment reply composer (opened by the Reply quick-action icon).
  const [replyingToId, setReplyingToId] = useState<string | null>(null);
  const [replyDraft, setReplyDraft] = useState("");
  const [privateReplyingToId, setPrivateReplyingToId] = useState<string | null>(null);
  const [privateReplyDraft, setPrivateReplyDraft] = useState("");

  const [showSidePanel, setShowSidePanel] = useState(false);
  const [editingRule, setEditingRule] = useState<ModerationRule | null>(null);
  const [isSavingRule, setIsSavingRule] = useState(false);
  const [isEditingRule, setIsEditingRule] = useState(false);
  // In-flight flags so manual actions show a spinner / disable instead of
  // staying clickable (prevents double-fire). bulkRunning covers all bulk
  // toolbar actions; pendingRuleIds covers per-rule delete/toggle;
  // pendingQuickRuleNames covers the quick-rule switch + action dropdown.
  // Toasts are non-blocking, so bulk toolbar buttons stay enabled; kept as a
  // constant so existing `disabled={bulkRunning}` chrome still type-checks.
  const bulkRunning = false;
  const [pendingRuleIds, setPendingRuleIds] = useState<Set<string>>(new Set());
  const [pendingQuickRuleNames, setPendingQuickRuleNames] = useState<Set<string>>(new Set());
  const [newRule, setNewRule] = useState<NewRuleType>({
    name: "",
    scope: "global",
    postId: "",
    triggerType: "contains",
    triggerValue: "",
    template: "",
    aiIntentDescription: "",
    aiIntentExamples: [],
    aiIntentCounterExamples: [],
    actions: [],
    language: "auto",
    useAI: false,
    isActive: true, // Default to active
  });

  // Test moderation state
  const [showTestModal, setShowTestModal] = useState(false);
  const [testComment, setTestComment] = useState("");
  const [testResult, setTestResult] = useState<any>(null);
  const [isTesting, setIsTesting] = useState(false);

  // Reply/DM modal state
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [replyMessage, setReplyMessage] = useState("");
  const [replyMessageType, setReplyMessageType] = useState<"static" | "ai">(
    "static"
  );
  const replyCommentId = "";
  const replyAction = "reply";
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  // Template options - now fetched from backend
  const [triggerTemplates, setTriggerTemplates] = useState([
    { value: "klem_zayed", label: "Klem Zayed", trigger: "klem zayed" },
    { value: "negative", label: "Negative Sentiment", trigger: "hate" },
  ]);

  // Single-select status filter. "all" = no status restriction (every status).
  // Sent to the backend as `?status=<value>`; mirrored client-side via
  // STATUS_FILTER_TO_MOD below. Replaces the old Hidden/Deleted/Failed toggles.
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  // Moderation statuses each filter resolves to; null = no restriction ("All").
  const selectedStatuses = useMemo<string[] | null>(
    () => STATUS_FILTER_TO_MOD[statusFilter],
    [statusFilter]
  );
  
  // Real-time incoming comments state
  const [incomingComments, setIncomingComments] = useState<Map<string, Comment>>(new Map());

  // Caps how many real-time comments animate concurrently. Anything beyond
  // this is rolled into the batched-count banner instead of stacking 5+
  // second animation chains on screen during traffic spikes.
  const MAX_VISIBLE_ANIMATIONS = 5;
  // If the user has scrolled past this many pixels we skip animations entirely
  // — they're not watching the top of the list, so the colored fly-ins are
  // wasted motion and just create memory pressure.
  const SCROLL_TOP_THRESHOLD_PX = 200;

  // Comments that arrived while the buffer was full or the user was scrolled
  // away. Shown as a single "N nouveaux commentaires modérés automatiquement"
  // banner; clicking it scrolls to top and refetches.
  const [batchedCount, setBatchedCount] = useState(0);

  // Mirrors `incomingComments.size` so the socket handler can read the count
  // without recapturing closures. Sync'd via the effect below.
  const incomingCountRef = useRef(0);
  useEffect(() => {
    incomingCountRef.current = incomingComments.size;
  }, [incomingComments.size]);

  // Tracks every setTimeout scheduled by the realtime animation pipeline so
  // we can drop them on unmount / page switch. Without this, fast page
  // navigation leaves orphaned setState chains queued for several seconds.
  const pendingAnimationTimeouts = useRef<Set<ReturnType<typeof setTimeout>>>(
    new Set()
  );

  // Filter management state
  const customDays = 7;

  // Counter for socket-pushed comments that didn't match the active filters and
  // were therefore not injected into the visible list. Surfaces a small "N new
  // comments hidden by filters" banner so the moderator still sees a signal.
  const [filteredOutSocketCount, setFilteredOutSocketCount] = useState(0);
  
  // Common Rules highlighting state
  const [highlightedRuleId, setHighlightedRuleId] = useState<string | null>(null);
  
  // Cursor-based pagination state
  const [commentCursor, setCommentCursor] = useState<string | null>(null);
  const [hasMoreComments, setHasMoreComments] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // New state variables for completely separate data flows
  // State for sidebar posts (completely separate from rule posts)
  const [sidebarPosts, setSidebarPosts] = useState<FacebookPost[]>([]);
  const [sidebarPostSearchQuery, setSidebarPostSearchQuery] = useState("");
  const [sidebarPostCursor, setSidebarPostCursor] = useState<string | null>(
    null
  );
  const [hasMoreSidebarPosts, setHasMoreSidebarPosts] = useState(true);
  const [sidebarPostPagination, setSidebarPostPagination] = useState<{
    totalCount: number;
    filteredCount: number;
    nextCursor: string | null;
  }>({
    totalCount: 0,
    filteredCount: 0,
    nextCursor: null,
  });
  const [loadingMoreSidebarPosts, setLoadingMoreSidebarPosts] = useState(false);

  // State for rule creation slide posts (completely separate from sidebar posts)
  const [rulePosts, setRulePosts] = useState<FacebookPost[]>([]);
  const [rulePostSearchQuery, setRulePostSearchQuery] = useState("");
  const [rulePostCursor, setRulePostCursor] = useState<string | null>(null);
  const [hasMoreRulePosts, setHasMoreRulePosts] = useState(true);
  const [rulePostPagination, setRulePostPagination] = useState<{
    totalCount: number;
    filteredCount: number;
    nextCursor: string | null;
  }>({
    totalCount: 0,
    filteredCount: 0,
    nextCursor: null,
  });
  const [loadingMoreRulePosts, setLoadingMoreRulePosts] = useState(false);

  // Load data when pageId changes
  useEffect(() => {
    if (pageId) {
      console.log("🔄 [PAGE_CHANGE] Page ID changed to:", pageId);
      currentPageIdRef.current = pageId;
      isInitialLoad.current = true; // Mark as initial load

      (async () => {
        try {
          await loadData(true);
          await loadAITemplates();
        } finally {
          isInitialLoad.current = false; // Initial load complete
          console.log("Done from useEffect");
        }
      })();
    }
  }, [pageId]);

  // Reload comments when post filter changes (but not on initial load)
  useEffect(() => {
    if (pageId && !isInitialLoad.current && selectedSection !== undefined) {
      console.log("🔄 [SECTION_CHANGE] Selected section changed to:", selectedSection);
      // Reload comments with new post filter (don't force sync, just reload from database)
      loadCommentsOnly(true, false);
    }
  }, [selectedSection, pageId]);

  // Auto-apply other filter changes — search/date/rules/visibility flags.
  // Skips the initial mount; the pageId effect above does the first load.
  // The search input is already debounced (~500ms in debouncedCommentSearch),
  // so changes to commentSearchQuery here are throttled at the source.
  const filterAutoApplyMounted = useRef(false);
  useEffect(() => {
    if (!filterAutoApplyMounted.current) {
      filterAutoApplyMounted.current = true;
      return;
    }
    if (!pageId || isInitialLoad.current) return;
    loadCommentsOnly(true, false);
  }, [
    commentSearchQuery,
    commentDateFilter,
    selectedRuleFilters,
    statusFilter,
  ]);

  // Listen for page changes from the top bar selector
  useEffect(() => {
    const handlePageChange = (event: CustomEvent) => {
      const newPageId = event.detail.pageId;
      console.log(
        "🔄 [PAGE_SELECTOR] Page change detected:",
        newPageId,
        "Current pageId:",
        pageId
      );

      // If the page ID is different, we need to wait for the URL to update
      // The URL will be updated by the FacebookPageSelector component
      // We'll let the useEffect that watches pageId handle the data loading
      if (newPageId !== pageId) {
        console.log(
          "🔄 [PAGE_SELECTOR] Page ID changed, waiting for URL update..."
        );
        console.log(
          "🔄 [PAGE_SELECTOR] FacebookPageSelector will update URL to:",
          `/accounts/${newPageId}/moderation`
        );
        // The URL will be updated by FacebookPageSelector, which will trigger the pageId useEffect
      }
    };

    window.addEventListener(
      "facebookPageChanged",
      handlePageChange as EventListener
    );

    return () => {
      window.removeEventListener(
        "facebookPageChanged",
        handlePageChange as EventListener
      );
    };
  }, [pageId]);

  // The socket effect below only re-subscribes when pageId changes, so its
  // handler closures would otherwise read stale filter state. Mirror the
  // active filter values into a ref so the predicate always sees current
  // filters when a comment arrives.
  const activeFiltersRef = useRef({
    selectedSection: "global" as "global" | string,
    commentSearchQuery: "",
    commentDateFilter: "all",
    selectedStatuses: null as string[] | null,
  });
  useEffect(() => {
    activeFiltersRef.current = {
      selectedSection,
      commentSearchQuery,
      commentDateFilter,
      selectedStatuses,
    };
    // The banner counter is tied to the filters that were active when items
    // were dropped; if filters changed, the count is no longer meaningful.
    setFilteredOutSocketCount(0);
  }, [
    selectedSection,
    commentSearchQuery,
    commentDateFilter,
    selectedStatuses,
  ]);

  // Banner click: scroll back to the top of the comment list and refetch so
  // the user actually sees the comments they missed. Refetch is necessary
  // because realtime events that get gated never enter `comments`.
  const showBatchedComments = () => {
    setBatchedCount(0);
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    loadCommentsOnly(true, false);
  };

  const clearAllCommentFilters = () => {
    // Banner click — reset every active filter so the user lands back on the
    // default view ("All" statuses). Resetting the status filter to "all"
    // widens the view back to everything.
    setSelectedSection("global");
    setCommentSearchQuery("");
    setCommentDateFilter("all");
    setStatusFilter("all");
    setSelectedRuleFilters([]);
    setFilteredOutSocketCount(0);
  };

  // Set up Socket.IO connection for real-time comment updates
  useEffect(() => {
    if (!pageId) return;

    console.log('🔌 [SOCKET] Setting up real-time connection for page:', pageId);

    // Fresh page: drop any stale batched count from the previous page so the
    // banner doesn't carry over.
    setBatchedCount(0);

    // Join the page-specific room
    joinPageRoom(pageId);

    // Schedules a setTimeout that auto-deregisters when it fires. The cleanup
    // below clears anything still pending so we don't leak setState calls into
    // a later-mounted component or another page.
    const trackedTimeout = (fn: () => void, ms: number) => {
      const id = setTimeout(() => {
        pendingAnimationTimeouts.current.delete(id);
        fn();
      }, ms);
      pendingAnimationTimeouts.current.add(id);
      return id;
    };

    // Mirror the backend's default-list predicate on the client so we don't
    // inject comments that the current filtered view would have excluded.
    // Note: rule-id filtering is intentionally not evaluated here — the
    // backend rule filter is post-hoc/expensive; on the next fetch the list
    // re-aligns naturally.
    const passesActiveFilters = (c: any): boolean => {
      const f = activeFiltersRef.current;

      if (f.selectedSection !== "global" && c.postId !== f.selectedSection) {
        return false;
      }

      // Status filter mirrors the backend: a non-null status list restricts to
      // those statuses; null ("All") imposes no status restriction.
      const status = c.status || c.moderationStatus;
      if (f.selectedStatuses && !f.selectedStatuses.includes(status)) {
        return false;
      }

      if (f.commentDateFilter && f.commentDateFilter !== "all") {
        const created = new Date(
          c.created_time || c.createdAt || Date.now()
        ).getTime();
        const dayMs = 24 * 60 * 60 * 1000;
        let cutoffMs = 0;
        switch (f.commentDateFilter) {
          case "yesterday":
            cutoffMs = dayMs;
            break;
          case "week":
            cutoffMs = 7 * dayMs;
            break;
          case "month":
            cutoffMs = 30 * dayMs;
            break;
          case "custom":
            cutoffMs = 7 * dayMs;
            break;
        }
        if (cutoffMs && created < Date.now() - cutoffMs) return false;
      }

      const q = (f.commentSearchQuery || "").trim().toLowerCase();
      if (q) {
        const msg = (c.message || "").toLowerCase();
        const author = (c.from?.name || "").toLowerCase();
        if (!msg.includes(q) && !author.includes(q)) return false;
      }

      return true;
    };

    // Handle incoming comments
    const handleNewComment = (comment: Comment) => {
      console.log('🔌 [SOCKET] Received new comment:', comment);
      console.log('🔌 [SOCKET] Comment details:', {
        id: comment.id,
        status: comment.status,
        moderationAction: comment.moderationResult?.action,
        fullModerationResult: comment.moderationResult
      });

      if (!passesActiveFilters(comment)) {
        setFilteredOutSocketCount((n) => n + 1);
        return;
      }

      // Throttle visual chaos: if too many animations are already on screen,
      // or the user has scrolled away from the top, roll this comment into
      // the batched-count banner instead of starting another 5s animation
      // chain. The banner click reloads from the server so nothing is lost.
      const scrollY =
        typeof window !== "undefined" ? window.scrollY || 0 : 0;
      const userScrolledAway = scrollY > SCROLL_TOP_THRESHOLD_PX;
      const bufferFull =
        incomingCountRef.current >= MAX_VISIBLE_ANIMATIONS;
      if (userScrolledAway || bufferFull) {
        setBatchedCount((n) => n + 1);
        return;
      }

      // Mark comment as new and processing
      // Also ensure we capture the correct status from backend
      const newComment: Comment = {
        ...comment,
        isNew: true,
        isProcessing: true,
        animationState: "entering",
        // Preserve the status sent from backend
        status: comment.status || 'pending'
      };

      // Optimistically bump the count so back-to-back socket events in the
      // same tick see the updated buffer size before React reconciles state.
      incomingCountRef.current += 1;

      // Add to incoming comments map
      setIncomingComments((prev) => {
        const updated = new Map(prev);
        updated.set(comment.id, newComment);
        return updated;
      });

      // After 1.5 seconds, mark as processed and apply action animation
      trackedTimeout(() => {
        setIncomingComments((prev) => {
          const updated = new Map(prev);
          const existing = updated.get(comment.id);
          if (existing) {
            // Use the action from moderationResult, or fall back to status
            const action = comment.moderationResult?.action || comment.status || 'approved';
            const newStatus = action === 'delete' ? 'deleted' :
                             action === 'hide' ? 'hidden' :
                             action === 'approved' ? 'approved' :
                             comment.status || 'approved';

            console.log('🔌 [SOCKET] Processing comment:', {
              id: comment.id,
              action,
              newStatus,
              animationState: action
            });

            updated.set(comment.id, {
              ...existing,
              isProcessing: false,
              animationState: action as any,
              // Ensure status is set correctly for display
              status: newStatus
            });
          }
          return updated;
        });

        // If action is delete or hide, remove after showing colored state
        const action = comment.moderationResult?.action;
        if (action === 'delete' || action === 'hide') {
          // Show colored background for 3 seconds so user can see it clearly
          trackedTimeout(() => {
            setIncomingComments((prev) => {
              const updated = new Map(prev);
              const existing = updated.get(comment.id);
              if (existing) {
                updated.set(comment.id, {
                  ...existing,
                  animationState: "removing"
                });
              }
              return updated;
            });

            // Finally remove from incoming comments after fade-out
            trackedTimeout(() => {
              setIncomingComments((prev) => {
                const updated = new Map(prev);
                updated.delete(comment.id);
                return updated;
              });
            }, 800); // Fade-out duration
          }, 1800); // Hold the shield-wipe / censor sweep briefly, then slide out
        } else {
          // For approved comments, add to main comments list after animation
          trackedTimeout(() => {
            setComments((prevComments) => {
              // Check if comment already exists
              if (prevComments.some(c => c.id === comment.id)) {
                return prevComments;
              }
              // Add to top of the list
              return [{ ...comment, isNew: false, isProcessing: false }, ...prevComments];
            });

            // Remove from incoming comments
            setIncomingComments((prev) => {
              const updated = new Map(prev);
              updated.delete(comment.id);
              return updated;
            });
          }, 2000); // Show approved state for 2 seconds
        }
      }, 1500); // Show loading state for 1.5 seconds
    };

    // Handle remote status changes (another client/tab moderating the same page)
    const handleCommentUpdated = (update: {
      id: string;
      status?: string;
      action?: string;
      moderationResult?: any;
      autoReply?: any;
    }) => {
      console.log("🔌 [SOCKET] comment-updated received:", update);
      setComments((prev) =>
        prev.map((c) =>
          c.id === update.id
            ? {
                ...c,
                status: (update.status as Comment["status"]) ?? c.status,
                moderationResult: update.moderationResult || c.moderationResult,
                autoReply: update.autoReply ?? c.autoReply,
              }
            : c
        )
      );
    };

    // Subscribe to socket events
    onNewComment(handleNewComment);
    onCommentUpdated(handleCommentUpdated);

    // Cleanup on unmount or page change
    return () => {
      console.log('🔌 [SOCKET] Cleaning up connection for page:', pageId);
      offNewComment(handleNewComment);
      offCommentUpdated(handleCommentUpdated);
      leavePageRoom(pageId);
      // Drop every queued animation step so they don't fire setState into a
      // stale page or, worse, an unmounted component.
      pendingAnimationTimeouts.current.forEach(clearTimeout);
      pendingAnimationTimeouts.current.clear();
      incomingCountRef.current = 0;
    };
  }, [pageId]);

  // Generic loader function that can be used for different post contexts
  const loadPaginatedPosts = async ({
    getQuery,
    getCursor,
    getItems,
    getPagination,
    setItems,
    setCursor,
    setHasMore,
    setPagination,
    setLoading,
    reset,
    signal,
  }: {
    getQuery: () => string;
    getCursor: () => string | null;
    getItems: () => FacebookPost[];
    getPagination: () => {
      totalCount: number;
      filteredCount: number;
      nextCursor: string | null;
    };
    setItems: (items: FacebookPost[]) => void;
    setCursor: (cursor: string | null) => void;
    setHasMore: (hasMore: boolean) => void;
    setPagination: (updater: any) => void;
    setLoading: (loading: boolean) => void;
    reset: boolean;
    signal?: AbortSignal;
  }) => {
    try {
      if (!pageId) return;
      if (pageId !== currentPageIdRef.current) return;

      setLoading(true);

      const params = new URLSearchParams();
      const prevCursor = getCursor();
      const cursorToUse = reset ? null : prevCursor;
      if (cursorToUse) params.append("cursor", cursorToUse);
      params.append("limit", "15");

      const searchToUse = getQuery();
      if (searchToUse) params.append("search", searchToUse);

      const postsRes = await api.get(
        `${API_ENDPOINTS.PAGE_POSTS(pageId!)}?${params.toString()}`,
        { signal }
      );

      const incomingPosts: FacebookPost[] = postsRes.data?.posts || [];
      const existing = reset ? [] : getItems();
      const existingIds = new Set(existing.map((p: FacebookPost) => p.id));
      const uniqueIncoming = incomingPosts.filter(
        (p: FacebookPost) => !existingIds.has(p.id)
      );
      const combinedPosts: FacebookPost[] = reset
        ? incomingPosts
        : [...existing, ...uniqueIncoming];

      const incomingTotal = postsRes.data?.pagination?.totalCount || 0;
      const incomingFiltered = postsRes.data?.pagination?.filteredCount || 0;
      const nextCursor = postsRes.data?.pagination?.nextCursor || null;
      const backendHasMore = !!postsRes.data?.pagination?.hasMore;

      const prevPagination = getPagination();
      const effectiveFiltered = reset
        ? incomingFiltered
        : Math.max(incomingFiltered, prevPagination?.filteredCount || 0);

      const sameCursor =
        !!prevCursor && !!nextCursor && prevCursor === nextCursor;
      const computedHasMore =
        backendHasMore &&
        !!nextCursor &&
        !sameCursor &&
        combinedPosts.length < effectiveFiltered;

      setItems(combinedPosts);
      setHasMore(computedHasMore);
      setCursor(nextCursor);
      setPagination(() => ({
        totalCount: incomingTotal,
        filteredCount: effectiveFiltered,
        nextCursor,
      }));
    } catch (err) {
      if (
        (err as any)?.name === "CanceledError" ||
        (err as any)?.message === "canceled"
      ) {
        // Request was canceled; ignore
      } else {
        console.error("Error loading posts:", err);
      }
    } finally {
      setLoading(false);
    }
  };

  // Wrapper functions for specific contexts
  const sidebarAbortRef = useRef<AbortController | null>(null);
  const ruleAbortRef = useRef<AbortController | null>(null);
  const commentsAbortRef = useRef<AbortController | null>(null);

  const loadSidebarPostsOnly = async (
    resetPagination = true,
    queryOverride?: string
  ) => {
    const searchQ =
      queryOverride !== undefined ? queryOverride : sidebarPostSearchQuery;
    if (resetPagination) {
      setSidebarPostCursor(null);
      setHasMoreSidebarPosts(true);
      setSidebarPostPagination({
        totalCount: 0,
        filteredCount: 0,
        nextCursor: null,
      });
    }
    if (sidebarAbortRef.current) sidebarAbortRef.current.abort();
    sidebarAbortRef.current = new AbortController();

    await loadPaginatedPosts({
      getQuery: () => searchQ,
      getCursor: () => (resetPagination ? null : sidebarPostCursor),
      getItems: () => sidebarPosts,
      getPagination: () => sidebarPostPagination,
      setItems: setSidebarPosts,
      setCursor: setSidebarPostCursor,
      setHasMore: setHasMoreSidebarPosts,
      setPagination: setSidebarPostPagination,
      setLoading: setLoadingMoreSidebarPosts,
      reset: resetPagination,
      signal: sidebarAbortRef.current.signal,
    });
  };

  // Keep a ref to the latest sidebar loader so the debounced searcher (created
  // once) always calls the current closure rather than a stale first-render one.
  const loadSidebarPostsRef = useRef(loadSidebarPostsOnly);
  loadSidebarPostsRef.current = loadSidebarPostsOnly;

  // Debounced post-filter search — one request after typing stops, instead of
  // one Facebook-backed request per keystroke.
  const debouncedSidebarPostSearchRef = useRef<((q: string) => void) | null>(null);
  if (!debouncedSidebarPostSearchRef.current) {
    debouncedSidebarPostSearchRef.current = debounce(
      (query: string) => loadSidebarPostsRef.current(true, query),
      400
    );
  }

  const loadRulePostsOnly = async (
    resetPagination = true,
    queryOverride?: string
  ) => {
    const searchQ =
      queryOverride !== undefined ? queryOverride : rulePostSearchQuery;
    if (resetPagination) {
      setRulePostCursor(null);
      setHasMoreRulePosts(true);
      setRulePostPagination({
        totalCount: 0,
        filteredCount: 0,
        nextCursor: null,
      });
    }
    if (ruleAbortRef.current) ruleAbortRef.current.abort();
    ruleAbortRef.current = new AbortController();

    await loadPaginatedPosts({
      getQuery: () => searchQ,
      getCursor: () => (resetPagination ? null : rulePostCursor),
      getItems: () => rulePosts,
      getPagination: () => rulePostPagination,
      setItems: setRulePosts,
      setCursor: setRulePostCursor,
      setHasMore: setHasMoreRulePosts,
      setPagination: setRulePostPagination,
      setLoading: setLoadingMoreRulePosts,
      reset: resetPagination,
      signal: ruleAbortRef.current.signal,
    });
  };

  // Create specific search handlers (hooks)


  // Load comments only with pagination and dedupe
  const loadCommentsOnly = async (
    resetPagination = true,
    forceSync = false
  ) => {
    // Cancel any in-flight comment fetch — its result would be stale and could
    // overwrite the response from this newer call (filter/section race).
    if (commentsAbortRef.current) commentsAbortRef.current.abort();
    const abortController = new AbortController();
    commentsAbortRef.current = abortController;

    try {
      if (!pageId) return;
      if (pageId !== currentPageIdRef.current) return;

      // Set syncing state if force sync is requested
      if (forceSync) {
        setIsSyncing(true);
      }

      if (resetPagination) {
        // Clear visible rows synchronously so stale comments from the previous
        // section/filter don't linger while the new query is in flight.
        setComments([]);
        setCommentCursor(null);
        setHasMoreComments(true);
      }

      const params = new URLSearchParams();
      const cursorToUse = resetPagination ? null : commentCursor;
      if (cursorToUse) params.append("cursor", cursorToUse);
      params.append("limit", "20");

      if (commentSearchQuery) params.append("search", commentSearchQuery);
      if (commentDateFilter && commentDateFilter !== "all")
        params.append("dateFilter", commentDateFilter);
      if (commentDateFilter === "custom" && customDays)
        params.append("customDays", String(customDays));
      if (selectedRuleFilters.length > 0)
        selectedRuleFilters.forEach((id) => params.append("ruleIds", id));
      params.append("status", statusFilter);
      if (selectedSection && selectedSection !== "global")
        params.append("postId", selectedSection);

      // Add sync parameter if force sync is requested
      if (forceSync) {
        params.append("sync", "true");
      }

      const commentsRes = await api.get(
        `${API_ENDPOINTS.PAGE_COMMENTS(pageId!)}?${params.toString()}`,
        { signal: abortController.signal }
      );

      // If a newer fetch superseded this one mid-flight, drop the result.
      if (commentsAbortRef.current !== abortController) return;

      const incomingComments: Comment[] = commentsRes.data?.comments || [];
      const existing = resetPagination ? [] : comments;
      const existingIds = new Set(existing.map((c) => c.id));
      const uniqueIncoming = incomingComments.filter(
        (c) => !existingIds.has(c.id)
      );
      const combinedComments = resetPagination
        ? incomingComments
        : [...existing, ...uniqueIncoming];

      const cNextCursor = commentsRes.data?.pagination?.nextCursor || null;
      const cBackendHasMore = !!commentsRes.data?.pagination?.hasMore;
      const cSameCursor =
        !!commentCursor && !!cNextCursor && commentCursor === cNextCursor;
      // Cursor pagination: the backend fetches limit+1 and sets hasMore on
      // overflow, so backendHasMore + a fresh (non-repeating) cursor is the
      // authoritative "next page exists" signal. We intentionally do NOT gate on
      // pagination.filteredCount here — for the comments endpoint that value is
      // per-page (≤ limit), not the filtered total, so comparing it against the
      // accumulated list disabled "Load more" after the very first page.
      const cHasMore = cBackendHasMore && !!cNextCursor && !cSameCursor;

      setComments(combinedComments);
      setHasMoreComments(cHasMore);
      setCommentCursor(cNextCursor);
      setLastLoadedAt(Date.now());

      // Log section change for debugging
      if (!forceSync && resetPagination) {
        console.log(`🔍 [COMMENTS] Loaded ${incomingComments.length} comments for section:`, selectedSection || 'all');
      }
    } catch (err: any) {
      // Aborted requests are expected when filters change rapidly — not an error.
      if (err?.name === "CanceledError" || err?.name === "AbortError" || err?.code === "ERR_CANCELED") {
        return;
      }
      console.error("Error loading comments:", err);
      if (forceSync) {
        alert(`❌ Failed to sync comments from ${platformName}. Please try again.`);
      }
    } finally {
      // Only the latest request should clear the loading flags; otherwise an
      // aborted older request would prematurely turn the spinner off.
      if (commentsAbortRef.current === abortController) {
        setIsSyncing(false);
        setLoadingMore(false);
        setIsLoading(false);
      }
    }
  };

  const loadData = async (resetPagination = true) => {
    try {
      // Validate pageId before proceeding
      if (!pageId) {
        console.warn("No pageId provided, skipping data load");
        return;
      }

      if (pageId !== currentPageIdRef.current) {
        console.warn(
          "🔄 [LOAD_DATA] Page ID mismatch - current:",
          pageId,
          "ref:",
          currentPageIdRef.current
        );
        return;
      }

      console.log("🔄 [LOAD_DATA] Starting data load for pageId:", pageId);
      console.log("🔄 [LOAD_DATA] Reset pagination:", resetPagination);

      if (resetPagination) {
        setIsLoading(true);
        // Reset comments pagination
        setCommentCursor(null);
        setHasMoreComments(true);
        // Reset posts contexts
        setSidebarPostCursor(null);
        setHasMoreSidebarPosts(true);
        setRulePostCursor(null);
        setHasMoreRulePosts(true);
      } else {
        setLoadingMore(true);
      }
      setError(null);

      // Load page, rules, and statistics data
      const [pageRes, rulesRes, statsRes] = await Promise.all([
        api.get(API_ENDPOINTS.FACEBOOK_PAGE_DETAIL(pageId!)),
        api.get(API_ENDPOINTS.MODERATION_RULES(pageId!)),
        api.get(API_ENDPOINTS.MODERATION_STATS(pageId!)).catch((err) => {
          console.error("❌ [LOAD_DATA] Failed to load statistics:", err);
          return { data: null };
        }),
      ]);

      setPage(pageRes.data);
      setRules(rulesRes.data || []);

      // Update statistics if successfully loaded
      if (statsRes.data) {
        setStatistics(statsRes.data);
        console.log("📊 [LOAD_DATA] Statistics loaded:", statsRes.data);
      } else {
        console.warn(
          "⚠️ [LOAD_DATA] Statistics not available, keeping defaults"
        );
      }

      // Kick off independent loads
      await Promise.all([
        loadCommentsOnly(resetPagination),
        resetPagination ? loadSidebarPostsOnly(true) : Promise.resolve(),
      ]);
    } catch (e) {
      console.error("Error in loadData:", e);
      setError("Failed to load data");
    } finally {
      setLoadingMore(false);
      setIsLoading(false);
    }
  };

  // Load AI moderation templates from backend
  const loadAITemplates = async () => {
    try {
      const response = await api.get(
        API_ENDPOINTS.AI_MODERATION_STATUS(pageId!)
      );
      if (response.data.success && response.data.templates) {
        setTriggerTemplates(response.data.templates);
      }
    } catch (error) {
      console.error("Error loading AI templates:", error);
      // Keep default templates if loading fails
    }
  };

  // Load more comments
  const loadMoreComments = async () => {
    if (!hasMoreComments || loadingMore) return;
    await loadData(false);
  };

  // Top stat cards double as quick status filters. Clicking an active card
  // toggles it back off (returns to the default pending+approved view). The
  // status flags feed selectedStatuses, which the auto-apply effect refetches.
  const handleStatCardClick = (key: ImpactCardKey) => {
    switch (key) {
      case "comments":
        setStatusFilter("all");
        break;
      case "hidden":
        // Toggle: clicking the active card returns to "All".
        setStatusFilter(statusFilter === "hidden" ? "all" : "hidden");
        break;
      case "deleted":
        setStatusFilter(statusFilter === "deleted" ? "all" : "deleted");
        break;
      case "moderated24h":
        // Single-select can't express hidden+deleted; map to Hidden (the most
        // common moderation outcome) and toggle back to "All".
        setStatusFilter(statusFilter === "hidden" ? "all" : "hidden");
        break;
    }
  };

  const activeImpactKey: ImpactCardKey =
    statusFilter === "hidden"
      ? "hidden"
      : statusFilter === "deleted"
      ? "deleted"
      : "comments";

  // Send an inline reply from a comment's Reply quick-action composer.
  const submitInlineReply = async (commentId: string) => {
    const msg = replyDraft.trim();
    if (!msg) return;
    await handleCommentAction(commentId, "reply", msg, "static");
    setReplyingToId(null);
    setReplyDraft("");
  };

  // Private reply (Instagram: one private DM per comment within the messaging
  // window; Facebook: page-to-commenter private message). Reuses the existing
  // 'dm' action path.
  const submitInlinePrivateReply = async (commentId: string) => {
    const msg = privateReplyDraft.trim();
    if (!msg) return;
    await handleCommentAction(commentId, "dm", msg, "static");
    setPrivateReplyingToId(null);
    setPrivateReplyDraft("");
  };

  // Handle applying filters - reset pagination and reload data
  // Check if there are any active filters (regardless of pending state)
  const hasActiveCommentFilters = () => {
    return (
      commentSearchQuery !== "" ||
      selectedRuleFilters.length > 0 ||
      commentDateFilter !== "all" ||
      statusFilter !== "all"
    );
  };

  const handleClearCommentFilters = () => {
    // Reset filter states
    setCommentSearchQuery("");
    setSelectedRuleFilters([]);
    setCommentDateFilter("all");
    setStatusFilter("all");

    // Reset pagination flags so the auto-apply effect refetches from page 1.
    setCommentCursor(null);
    setHasMoreComments(true);
    setSidebarPostCursor(null);
    setHasMoreSidebarPosts(true);
    setRulePostCursor(null);
    setHasMoreRulePosts(true);
    // Comment list reload is triggered by the auto-apply useEffect that
    // watches the filter state.
  };

  // Get potential rule matches for a comment (for preview filtering)
  const getPotentialRuleMatches = (comment: Comment) => {
    if (!comment || !sectionRules.length) return [];

    return sectionRules.filter((rule) => {
      if (!rule.isActive) return false;

      // For post-specific rules, only apply if they match the comment's post
      if (rule.scope === "post" && rule.postId !== comment.postId) {
        return false;
      }

      // Check if rule matches the comment
      return doesRuleMatchComment(rule, comment);
    });
  };

  // Check if a comment has rules already applied to it
  const hasAppliedRules = (comment: Comment) => {
    return (
      comment?.moderationResult?.matchedRules &&
      comment.moderationResult.matchedRules.length > 0
    );
  };

  // Check if a comment would match selected rules (for preview)
  const wouldMatchSelectedRules = (comment: Comment) => {
    if (!selectedRuleFilters.length) return false;

    const potentialMatches = getPotentialRuleMatches(comment);
    return potentialMatches.some((rule) =>
      selectedRuleFilters.includes(rule.id)
    );
  };

  // Helper function to get rule names for display

  // Helper function to safely extract user information from comment.
  // Facebook's Graph API does not return `from` for commenters who haven't
  // authorized this app (Meta policy since v17). When that happens we render
  // a neutral "Facebook user" label and surface a link to the post on Facebook
  // so the page owner can identify the commenter natively.
  // Commenter identity (name/handle/avatar seed) now lives in a shared util so
  // the Moderation Dashboard and the Review Queue render identical fingerprints.
  const getCommentUserInfo = getCommenterIdentity;

  // Helper function to check which rules match a comment

  // Get rules for selected section (show global rules + post-specific rules for selected post)
  // Control whether to include post-specific rules when viewing global rules
  const [showPostRulesInGlobal, setShowPostRulesInGlobal] = useState(false);

  // Common Rules names for filtering
  const quickRuleNames = quickRules.map(qr => qr.name);

  const sectionRules =
    selectedSection === "global"
      ? showPostRulesInGlobal
        ? rules.filter((r) => !quickRuleNames.includes(r.name || ''))
        : rules.filter((r) => r.scope === "global" && !quickRuleNames.includes(r.name || ''))
      : rules.filter(
          (r) =>
            (r.scope === "global" ||
            (r.scope === "post" && r.postId === selectedSection)) &&
            !quickRuleNames.includes(r.name || '')
        );

  // Debug logging for rules
  console.log(`🔍 [FRONTEND] Total rules loaded: ${rules.length}`);
  console.log(
    `🔍 [FRONTEND] Section rules (${selectedSection}): ${sectionRules.length}`
  );
  console.log(
    `🔍 [FRONTEND] Active rules: ${
      sectionRules.filter((r) => r.isActive).length
    }`
  );
  if (sectionRules.length > 0) {
    console.log(
      `🔍 [FRONTEND] Section rule details:`,
      sectionRules.map((r) => ({
        id: r.id,
        name: r.name,
        scope: r.scope,
        isActive: r.isActive,
        trigger: r.trigger,
      }))
    );
  }

  // Filter rules based on current mode and search query

  // Get selected post details
  const currentSelectedPost =
    selectedSection !== "global"
      ? sidebarPosts.find((p) => p.id === selectedSection)
      : null;

  // Backend already applies status/post/search/date filters server-side. The
  // client filter below is a defensive second pass so socket-pushed comments
  // and any stale rows are also subject to the active chips — keeping client
  // and server views in lockstep.
  const filteredComments = comments.filter((comment) => {
    const status = comment.status as string;
    // null selectedStatuses ("All") imposes no restriction.
    if (selectedStatuses && !selectedStatuses.includes(status)) return false;

    if (selectedSection !== "global" && comment.postId !== selectedSection) {
      return false;
    }

    return true;
  });

  // COMMENTED OUT FOR FACEBOOK REVIEW - AI reply generation hidden
  // Prefixed with underscore to indicate intentionally unused
  const _generateAIReply = async (commentId: string) => {
    setIsGeneratingAI(true);
    try {
      const comment = comments.find((c) => c.id === commentId);
      if (!comment) {
        alert("Comment not found");
        return;
      }

      console.log(
        `🤖 [FRONTEND] Generating AI reply for comment: "${comment.message}"`
      );

      // Call AI service to generate reply
      const response = await api.post(API_ENDPOINTS.COMMENT_AI_REPLY(pageId!), {
        comment: comment.message,
        action: replyAction, // Use the current reply action (reply or dm)
      });

      if (response.data && response.data.aiReply) {
        setReplyMessage(response.data.aiReply);
        setReplyMessageType("ai");
        console.log(
          `🤖 [FRONTEND] AI reply generated: "${response.data.aiReply}"`
        );
      } else {
        alert("Failed to generate AI reply");
      }
    } catch (error) {
      console.error("Error generating AI reply:", error);
      alert("Failed to generate AI reply");
    } finally {
      setIsGeneratingAI(false);
    }
  };

  // Executes ONE moderation action against Facebook with optimistic update +
  // rollback + error enrichment. Returns a result for the progress toast; it
  // shows no UI of its own (no alert/modal).
  const executeSingleAction = async (
    commentId: string,
    action: string,
    message?: string,
    messageType?: "static" | "ai"
  ): Promise<RunItemResult> => {
    // Snapshot current state so we can revert if the API call fails. The row
    // can be in either `comments` or the realtime `incomingComments` buffer;
    // the moderation buttons live on the persisted list, so we read from there.
    const targetComment = comments.find((c) => c.id === commentId);
    const previousStatus = targetComment?.status;
    const previousActionTaken = targetComment?.actionTaken;

    // Optimistic status: reply/dm don't change visibility status, so we leave
    // it untouched (the existing row status holds — usually 'approved').
    const optimisticStatus =
      action === "approve"
        ? "approved"
        : action === "hide"
        ? "hidden"
        : action === "delete"
        ? "deleted"
        : previousStatus;
    const optimisticActionTaken =
      action === "approve" || action === "hide" || action === "delete"
        ? (action as "approve" | "hide" | "delete")
        : previousActionTaken;

    // Apply the optimistic update immediately and clear any prior failure
    // markers so a retry doesn't keep showing the old error chrome.
    setComments((prev) =>
      prev.map((c) =>
        c.id === commentId
          ? {
              ...c,
              status: (optimisticStatus ?? "pending") as Comment["status"],
              actionTaken: optimisticActionTaken,
              isProcessing: true,
              processingAction: action as Comment["processingAction"],
              previousStatus,
              lastFailedAction: undefined,
              lastFailedMessage: undefined,
              lastFailedMessageType: undefined,
              lastError: undefined,
            }
          : c
      )
    );

    try {
      // Prepare request body with message and AI generation parameters
      const requestBody: any = {};
      if (message) {
        requestBody.message = message;
      }

      // Add AI generation parameters if this is an AI-generated message
      if (messageType === "ai" && (action === "reply" || action === "dm")) {
        requestBody.useAI = true;
        requestBody.messageType = "ai";
        // Add page information for AI generation
        if (page) {
          requestBody.pagePrompt = page.prompt || "";
          requestBody.pageName = page.name || "Page";
          requestBody.language = "auto"; // Default language since page.language doesn't exist
        }
        console.log(`🤖 [FRONTEND] AI generation enabled for ${action} action`);
      } else {
        console.log(`💬 [FRONTEND] Static message for ${action} action`);
      }

      console.log(`🔍 [FRONTEND] Sending ${action} action with:`, {
        message: message ? `"${message}"` : "none",
        messageType: messageType || "static",
        useAI: requestBody.useAI || false,
        pagePrompt: requestBody.pagePrompt || "none",
        pageName: requestBody.pageName || "none",
        language: requestBody.language || "none",
      });

      console.log(
        `🔍 [FRONTEND] Full request body:`,
        JSON.stringify(requestBody, null, 2)
      );

      const response = await api.post(
        API_ENDPOINTS.COMMENT_ACTION(pageId!, commentId, action),
        requestBody
      );

      // Success — keep the optimistic status and clear rollback metadata.
      setComments((prev) =>
        prev.map((c) =>
          c.id === commentId
            ? {
                ...c,
                isProcessing: false,
                processingAction: undefined,
                previousStatus: undefined,
                lastFailedAction: undefined,
                lastFailedMessage: undefined,
                lastFailedMessageType: undefined,
                lastError: undefined,
              }
            : c
        )
      );

      // Surface any backend warnings for secondary actions.
      if (response.data?.warnings && response.data.warnings.length > 0) {
        console.warn('Some secondary actions failed:', response.data.warnings);
      }
      return { success: true };
    } catch (err: any) {
      console.error(`Error ${action}ing comment:`, err);

      // Show more specific error messages
      let errorMessage = getActionMessage(action, 'error');

      if (err.response?.data?.error) {
        // Use the enhanced error message from backend
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }

      // Check for specific platform errors and provide helpful guidance
      const acct = isInstagram ? "Instagram account" : "Facebook page";
      if (errorMessage.includes("cannot be hidden") || errorMessage.includes("cannot be deleted")) {
        // Error 200 - Comment restriction (admin/account comment)
        errorMessage = `${errorMessage}\n\nℹ️ This is a ${platformName} restriction. The comment may be from:\n• The ${acct} itself\n• An administrator\n• A protected account\n\nThese comments cannot be moderated through the API.`;
      } else if (errorMessage.includes("24 hours") || errorMessage.includes("24-hour") || errorMessage.includes("7 day") || errorMessage.includes("private reply")) {
        // Private-reply window violation (FB: 24h messaging window; IG: one private reply per comment, within 7 days)
        errorMessage = isInstagram
          ? `${errorMessage}\n\nℹ️ Instagram Private Reply limits:\nYou can send only one private reply per comment, within 7 days of the comment.\n\n💡 Alternative:\n• Use the "Reply" button to post a public reply instead`
          : `${errorMessage}\n\nℹ️ Facebook Messaging Window Policy:\nDirect messages can only be sent within 24 hours of the user's last interaction with your page.\n\n💡 Alternative:\n• Use the "Reply" button to post a public comment instead\n• Wait for the user to interact with your page again`;
      } else if (
        errorMessage.includes("insufficient_scope") ||
        errorMessage.includes("permissions") ||
        FACEBOOK_PAGE_PERMISSIONS.some(perm => errorMessage.includes(perm))
      ) {
        // Permission errors - checking against centralized permission list
        errorMessage = `Permission Error: ${errorMessage}\n\n🔧 To fix this:\n1. Disconnect this ${acct}\n2. Reconnect and grant all permissions\n3. Try again`;
      } else if (errorMessage.includes("token expired")) {
        // Token expiration
        errorMessage = `${errorMessage}\n\n🔄 Please reconnect your ${acct} to continue.`;
      }

      // Roll back the optimistic update and stamp failure metadata so the row
      // can render the red border + retry button.
      setComments((prev) =>
        prev.map((c) =>
          c.id === commentId
            ? {
                ...c,
                status: (previousStatus ?? "pending") as Comment["status"],
                actionTaken: previousActionTaken,
                isProcessing: false,
                processingAction: undefined,
                previousStatus: undefined,
                lastFailedAction: action as Comment["lastFailedAction"],
                lastFailedMessage: message,
                lastFailedMessageType: messageType,
                lastError: errorMessage,
              }
            : c
        )
      );

      return {
        success: false,
        error: errorMessage,
        throttled:
          err.response?.status === 429 || !!err.response?.data?.throttled,
        retryMinutes: err.response?.data?.retryMinutes,
      };
    }
  };

  // Public entrypoint used by row buttons: runs a single action via the toast.
  const handleCommentAction = (
    commentId: string,
    action: string,
    message?: string,
    messageType?: "static" | "ai"
  ) => {
    const validActions = ["approve", "hide", "delete", "dm", "reply"];
    if (!validActions.includes(action)) {
      alert(`Invalid action type: ${action}`);
      return;
    }
    if ((action === "reply" || action === "dm") && !message) {
      alert(`Please provide a message for ${action} action`);
      return;
    }
    const c0 = comments.find((c) => c.id === commentId) as any;
    const handle = c0?.from?.name || c0?.author || "commenter";
    runModeration({
      pageId: pageId!,
      action,
      items: [{ id: commentId, handle }],
      runItem: (id) => executeSingleAction(id, action, message, messageType),
    });
  };

  // Re-runs the most recent failed action for a single comment. Reads the
  // saved action/message off the row so the user can retry without re-typing.
  const retryFailedAction = (comment: Comment) => {
    if (!comment.lastFailedAction) return;
    handleCommentAction(
      comment.id,
      comment.lastFailedAction,
      comment.lastFailedMessage,
      comment.lastFailedMessageType
    );
  };

  const handleBulkAction = async (action: string) => {
    // Validate action type
    const validActions = ["approve", "hide", "delete", "dm", "reply"];
    if (!validActions.includes(action)) {
      console.error(`Invalid action type: ${action}`);
      alert(`Invalid action type: ${action}`);
      return;
    }

    if (selectedComments.length === 0) {
      alert("Please select comments first");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to ${action} ${selectedComments.length} comments?`
      )
    )
      return;

    const bulkItems = selectedComments.map((id) => {
      const c0 = comments.find((c) => c.id === id) as any;
      return { id, handle: c0?.from?.name || c0?.author || "commenter" };
    });
    setSelectedComments([]);
    runModeration({
      pageId: pageId!,
      action,
      items: bulkItems,
      runItem: (id) => executeSingleAction(id, action),
    });
  };

  const handleBulkReply = (type: "reply" | "dm") => {
    if (selectedComments.length === 0) {
      alert("Please select comments to reply to");
      return;
    }
    const message = prompt(
      `Enter your ${type === "reply" ? "reply" : "private message"} for ${
        selectedComments.length
      } comments:`
    );
    if (!message?.trim()) return;
    const msg = message.trim();
    const bulkItems = selectedComments.map((id) => {
      const c0 = comments.find((c) => c.id === id) as any;
      return { id, handle: c0?.from?.name || c0?.author || "commenter" };
    });
    setSelectedComments([]);
    runModeration({
      pageId: pageId!,
      action: type,
      items: bulkItems,
      runItem: (id) => executeSingleAction(id, type, msg, undefined),
    });
  };

  // Bulk rule-application — preserved for reuse (e.g. an overflow action on the
  // contextual bar). Underscore-prefixed to mark it intentionally unused after
  // the toolbar redesign moved bulk actions to Reply/Hide/Delete only.
  const _handleApplyModerationRules = async (commentIds?: string[]) => {
    const commentsToProcess = commentIds || selectedComments;

    if (commentsToProcess.length === 0) {
      alert("Please select comments first");
      return;
    }

    if (
      !window.confirm(
        `Apply moderation rules to ${commentsToProcess.length} comments?`
      )
    )
      return;

    try {
      // Use the provided IDs to select comments
      const commentsToModerate = comments.filter((c) =>
        commentsToProcess.includes(c.id)
      );

      console.log(
        `🔧 [FRONTEND] Applying moderation rules to ${commentsToModerate.length} comments`
      );

      // Track successes and failures
      let successfulComments = 0;
      let failedComments = 0;
      let commentsWithNoRules = 0;
      const commentResults: { commentId: string; success: boolean; actionType?: string }[] = [];

      // Apply rules to each comment
      for (const comment of commentsToModerate) {
        console.log(
          `🔧 [FRONTEND] Processing comment: "${comment.message.substring(
            0,
            50
          )}..."`
        );

        // Find ALL applicable rules for this comment (both global and post-specific)
        const applicableRules = getApplicableRulesForComment(comment, rules);

        // Log matching rules for debugging
        applicableRules.forEach((rule) => {
          console.log(
            `🎯 [FRONTEND] Rule "${
              rule.name || rule.trigger.value
            }" matches comment`
          );
        });

        console.log(
          `🔧 [FRONTEND] Found ${applicableRules.length} matching rules for comment`
        );

        if (applicableRules.length > 0) {
          let commentSucceeded = true;
          let appliedActionType = "";

          // Apply ALL matching rules (not just the first one)
          for (const rule of applicableRules) {
            console.log(
              `🔧 [FRONTEND] Applying rule: "${
                rule.name || rule.trigger.value
              }"`
            );
            console.log(`🔧 [FRONTEND] Rule actions:`, rule.actions);

            // Validate rule actions before sending
            if (!validateRuleActions(rule)) {
              continue;
            }

            try {
              // Apply the rule's most severe action using the centralized moderation system
              const mostSevereAction = rule.actions.find(
                (a) => a.type === "delete"
              ) ||
                rule.actions.find((a) => a.type === "hide") ||
                rule.actions.find((a) => a.type === "reply") ||
                rule.actions.find((a) => a.type === "dm") ||
                rule.actions.find((a) => a.type === "approve") || {
                  type: "approve",
                };

              appliedActionType = mostSevereAction.type;

              const response = await api.post(
                API_ENDPOINTS.COMMENT_ACTION(
                  pageId!,
                  comment.id,
                  mostSevereAction.type
                ),
                {
                  source: "rule-application",
                  ruleId: rule.id,
                  ruleName: rule.name || rule.trigger.value,
                }
              );

              // Check if the response indicates success
              if (!response.data?.success) {
                commentSucceeded = false;
                console.error(
                  `❌ [FRONTEND] Rule application returned failure for "${
                    rule.name || rule.trigger.value
                  }"`
                );
              } else {
                console.log(
                  `✅ [FRONTEND] Successfully applied rule "${
                    rule.name || rule.trigger.value
                  }" with action "${mostSevereAction.type}"`
                );
              }
            } catch (ruleError: any) {
              commentSucceeded = false;
              console.error(
                `❌ [FRONTEND] Error applying rule "${
                  rule.name || rule.trigger.value
                }":`,
                ruleError
              );
              // Continue with other rules even if one fails
            }
          }

          // Track result for this comment
          commentResults.push({
            commentId: comment.id,
            success: commentSucceeded,
            actionType: appliedActionType
          });

          if (commentSucceeded) {
            successfulComments++;
            
            // Update comment status locally only if action succeeded
            const { type: mostSevereActionType, reason } =
              getMostSevereAction(applicableRules);

            setComments((prev) =>
              prev.map((c) =>
                c.id === comment.id
                  ? {
                      ...c,
                      status:
                        mostSevereActionType === "delete"
                          ? "deleted"
                          : mostSevereActionType === "hide"
                          ? "hidden"
                          : "approved",
                      actionTaken: mostSevereActionType as any,
                      moderationResult: {
                        action: mostSevereActionType,
                        reason: reason,
                        confidence: 1.0,
                        matchedRules: applicableRules.map((r) => r.id),
                      },
                    }
                  : c
              )
            );
          } else {
            failedComments++;
          }
        } else {
          console.log(`ℹ️ [FRONTEND] No matching rules found for comment`);
          commentsWithNoRules++;
        }
      }

      setSelectedComments([]);

      // Show accurate message based on results
      if (successfulComments === 0 && failedComments > 0) {
        // All failed
        alert(`Failed to apply moderation rules to ${failedComments} comments. Please check permissions and try again.`);
      } else if (failedComments > 0) {
        // Some succeeded, some failed
        alert(
          `Moderation rules applied to ${successfulComments} of ${commentsToModerate.length} comments successfully. ${failedComments} failed.`
        );
      } else if (commentsWithNoRules === commentsToModerate.length) {
        // No rules matched any comments
        alert(`No moderation rules matched the selected comments.`);
      } else {
        // All succeeded
        alert(
          `Moderation rules applied to ${successfulComments} comments successfully`
        );
      }
    } catch (err: any) {
      console.error(`Error applying moderation rules:`, err);
      alert(`Failed to apply moderation rules: ${err.message}`);
    }
  };

  const toggleCommentSelection = (commentId: string) => {
    setSelectedComments(
      selectedComments.includes(commentId)
        ? selectedComments.filter((id) => id !== commentId)
        : [...selectedComments, commentId]
    );
  };
  const testModeration = async () => {
    if (!testComment.trim()) {
      alert("Please enter a comment to test");
      return;
    }

    try {
      setIsTesting(true);
      const response = await api.post(API_ENDPOINTS.TEST_MODERATION(pageId!), {
        comment: testComment,
      });
      setTestResult(response.data.result);
    } catch (err: any) {
      console.error("Error testing moderation:", err);
      alert(
        "Failed to test comment: " + (err.response?.data?.error || err.message)
      );
    } finally {
      setIsTesting(false);
    }
  };

  const editRule = (rule: ModerationRule) => {
    setEditingRule(rule);
    setNewRule({
      name: rule.name || "",
      scope: rule.scope,
      postId: rule.postId || "",
      triggerType: rule.trigger.type,
      triggerValue: rule.trigger.value,
      template: rule.trigger.template || "",
      aiIntentDescription: rule.trigger.aiIntent?.description || "",
      aiIntentExamples: rule.trigger.aiIntent?.examples || [],
      aiIntentCounterExamples: rule.trigger.aiIntent?.counterExamples || [],
      actions: rule.actions as any,
      language: rule.language,
      useAI: rule.useAI ?? false, // Provide default if undefined
      isActive: rule.isActive,
    });
    setShowSidePanel(true);
  };

  const updateRule = async () => {
    const hasCondition =
      newRule.triggerType === "ai_intent"
        ? !!newRule.aiIntentDescription.trim()
        : newRule.triggerType === "template"
          ? !!newRule.template.trim()
          : !!newRule.triggerValue.trim();
    if (!editingRule || !newRule.name.trim() || !hasCondition) {
      alert("Please fill in all required fields");
      return;
    }

    if (newRule.scope === "post" && !newRule.postId) {
      alert("Please select a post for post-specific rules");
      return;
    }

    try {
      setIsEditingRule(true);

      console.log(
        "🔧 [FRONTEND] Updating rule with newRule.actions:",
        newRule.actions
      );

      const updatedRule: ModerationRule = {
        ...editingRule,
        name: newRule.name,
        scope: newRule.scope,
        postId: newRule.scope === "post" ? newRule.postId : undefined,
        trigger: {
          type: newRule.triggerType,
          value: newRule.triggerValue,
          template: newRule.template,
          ...(newRule.triggerType === "ai_intent"
            ? {
                aiIntent: {
                  description: newRule.aiIntentDescription.trim(),
                  examples: newRule.aiIntentExamples
                    .map((e) => e.trim())
                    .filter(Boolean),
                  counterExamples: newRule.aiIntentCounterExamples
                    .map((e) => e.trim())
                    .filter(Boolean),
                },
              }
            : {}),
        },
        actions: newRule.actions,
        language: newRule.language,
        // useAI is auto-determined by backend based on trigger type
      };

      console.log(
        "🔧 [FRONTEND] Sending updated rule to backend:",
        updatedRule
      );

      await api.put(
        `${API_ENDPOINTS.MODERATION_RULES(pageId!)}/${editingRule.id}`,
        updatedRule
      );

      // Refresh rules from server
      await loadData();

      alert("Rule updated successfully!");
      setEditingRule(null);
      setShowSidePanel(false);
      resetRuleForm();
    } catch (err: any) {
      console.error("Error updating rule:", err);
      alert(
        "Failed to update rule: " + (err.response?.data?.error || err.message)
      );
    } finally {
      setIsEditingRule(false);
    }
  };

  const deleteRule = async (ruleId: string) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this rule? This action cannot be undone."
      )
    ) {
      return;
    }

    if (pendingRuleIds.has(ruleId)) return;
    setPendingRuleIds((prev) => new Set(prev).add(ruleId));
    try {
      await api.delete(`${API_ENDPOINTS.MODERATION_RULES(pageId!)}/${ruleId}`);

      // Refresh rules from server
      await loadData();

      alert("Rule deleted successfully!");
    } catch (err: any) {
      console.error("Error deleting rule:", err);
      alert(
        "Failed to delete rule: " + (err.response?.data?.error || err.message)
      );
    } finally {
      setPendingRuleIds((prev) => {
        const next = new Set(prev);
        next.delete(ruleId);
        return next;
      });
    }
  };

  const toggleRuleStatus = async (ruleId: string, isActive: boolean) => {
    if (pendingRuleIds.has(ruleId)) return;
    setPendingRuleIds((prev) => new Set(prev).add(ruleId));
    try {
      const rule = rules.find((r) => r.id === ruleId);
      if (!rule) return;

      const updatedRule = { ...rule, isActive };
      await api.put(
        `${API_ENDPOINTS.MODERATION_RULES(pageId!)}/${ruleId}`,
        updatedRule
      );

      setRules((prev) =>
        prev.map((r) => (r.id === ruleId ? { ...r, isActive } : r))
      );
      alert(`Rule ${isActive ? "activated" : "deactivated"} successfully`);
    } catch (err: any) {
      console.error("Error toggling rule status:", err);
      alert(
        `Failed to ${isActive ? "activate" : "deactivate"} rule: ${err.message}`
      );
    } finally {
      setPendingRuleIds((prev) => {
        const next = new Set(prev);
        next.delete(ruleId);
        return next;
      });
    }
  };

  // Quick Rule Handlers
  const handleQuickRuleToggle = async (
    quickRule: QuickRule,
    checked: boolean,
    currentAction: string
  ) => {
    if (pendingQuickRuleNames.has(quickRule.name)) return;
    setPendingQuickRuleNames((prev) => new Set(prev).add(quickRule.name));
    try {
      const existingRule = rules.find((r) => r.name === quickRule.name);

      if (existingRule) {
        // Update existing rule status
        const updatedRule = { ...existingRule, isActive: checked };
        await api.put(
          `${API_ENDPOINTS.MODERATION_RULES(pageId!)}/${existingRule.id}`,
          updatedRule
        );
        
        setRules((prev) =>
          prev.map((r) => (r.id === existingRule.id ? { ...r, isActive: checked } : r))
        );
        
        // Highlight the rule when activated
        if (checked) {
          setHighlightedRuleId(existingRule.id);
          setTimeout(() => setHighlightedRuleId(null), 1500);
        }
      } else if (checked) {
        // Create new rule
        const newRuleData: Partial<ModerationRule> = {
          name: quickRule.name,
          scope: 'global',
          trigger: {
            type: quickRule.trigger.type,
            value: quickRule.trigger.value,
            template: quickRule.trigger.template
          },
          actions: [
            {
              type: currentAction as any,
            }
          ],
          language: 'auto',
          useAI: quickRule.useAI,
          isActive: true,
        };

        console.log('🚀 [QUICK_RULE] Creating new quick rule:', newRuleData);
        
        await api.post(
          API_ENDPOINTS.MODERATION_RULES(pageId!),
          newRuleData
        );
        
        // Refresh rules from server
        await loadData();
        
        // Find and highlight the newly created rule after a brief delay
        setTimeout(() => {
          const newRule = rules.find((r) => r.name === quickRule.name);
          if (newRule) {
            setHighlightedRuleId(newRule.id);
            setTimeout(() => setHighlightedRuleId(null), 1500);
          }
        }, 500);
      }
    } catch (err: any) {
      console.error('Error toggling quick rule:', err);
      alert(
        `Failed to ${checked ? 'activate' : 'deactivate'} quick rule: ${
          err.response?.data?.error || err.message
        }`
      );
    } finally {
      setPendingQuickRuleNames((prev) => {
        const next = new Set(prev);
        next.delete(quickRule.name);
        return next;
      });
    }
  };

  const handleQuickRuleActionChange = async (
    quickRule: QuickRule,
    newAction: string
  ) => {
    if (pendingQuickRuleNames.has(quickRule.name)) return;
    setPendingQuickRuleNames((prev) => new Set(prev).add(quickRule.name));
    try {
      const existingRule = rules.find((r) => r.name === quickRule.name);

      if (!existingRule) {
        alert('Rule not found. Please activate the rule first.');
        return;
      }

      const updatedRule = {
        ...existingRule,
        actions: [
          {
            type: newAction as any,
          }
        ]
      };

      console.log('🔄 [QUICK_RULE] Updating quick rule action:', {
        ruleName: quickRule.name,
        newAction
      });

      await api.put(
        `${API_ENDPOINTS.MODERATION_RULES(pageId!)}/${existingRule.id}`,
        updatedRule
      );

      setRules((prev) =>
        prev.map((r) =>
          r.id === existingRule.id
            ? { ...r, actions: updatedRule.actions }
            : r
        )
      );

      // Optional: Show success message
      console.log(`✅ [QUICK_RULE] Action updated to "${newAction}" for "${quickRule.name}"`);
    } catch (err: any) {
      console.error('Error updating quick rule action:', err);
      alert(
        `Failed to update action: ${err.response?.data?.error || err.message}`
      );
    } finally {
      setPendingQuickRuleNames((prev) => {
        const next = new Set(prev);
        next.delete(quickRule.name);
        return next;
      });
    }
  };

  const selectAllComments = () => {
    setSelectedComments(
      selectedComments.length === filteredComments.length
        ? []
        : filteredComments.map((c) => c.id)
    );
  };

  // Select every currently-visible comment matching a given status (drives the
  // "by status" options in the selection dropdown).
  const selectVisibleByStatus = (status: Comment["status"]) => {
    setSelectedComments(
      filteredComments.filter((c) => c.status === status).map((c) => c.id)
    );
  };

  // Enhanced function to select all comments including paginated ones
  const selectAllCommentsIncludingPaginated = async () => {
    if (selectedComments.length === comments.length && comments.length > 0) {
      // If all comments are selected, deselect all
      setSelectedComments([]);
      return;
    }

    // Already fully loaded — just select everything visible.
    if (!hasMoreComments) {
      setSelectedComments(comments.map((c) => c.id));
      return;
    }

    // Cancel any in-flight comment fetch so this loop owns the response stream.
    if (commentsAbortRef.current) commentsAbortRef.current.abort();
    const abort = new AbortController();
    commentsAbortRef.current = abort;

    setIsLoading(true);
    try {
      // Drive pagination directly from response cursors. React state captured
      // in this async closure (hasMoreComments, commentCursor, comments) won't
      // update between awaits, so looping on it would either spin forever or
      // refetch the same page.
      let cursor: string | null = commentCursor;
      const seen = new Set(comments.map((c) => c.id));
      const aggregated: Comment[] = [...comments];
      const MAX_PAGES = 200;

      for (let i = 0; i < MAX_PAGES && cursor; i++) {
        const params = new URLSearchParams();
        params.append("cursor", cursor);
        params.append("limit", "20");
        if (commentSearchQuery) params.append("search", commentSearchQuery);
        if (commentDateFilter && commentDateFilter !== "all")
          params.append("dateFilter", commentDateFilter);
        if (commentDateFilter === "custom" && customDays)
          params.append("customDays", String(customDays));
        if (selectedRuleFilters.length > 0)
          selectedRuleFilters.forEach((id) => params.append("ruleIds", id));
        params.append("status", statusFilter);
        if (selectedSection && selectedSection !== "global")
          params.append("postId", selectedSection);

        const res = await api.get(
          `${API_ENDPOINTS.PAGE_COMMENTS(pageId!)}?${params.toString()}`,
          { signal: abort.signal }
        );

        // A newer fetch superseded us — drop this result and stop.
        if (commentsAbortRef.current !== abort) return;

        const incoming: Comment[] = res.data?.comments || [];
        for (const c of incoming) {
          if (!seen.has(c.id)) {
            seen.add(c.id);
            aggregated.push(c);
          }
        }

        const nextCursor: string | null =
          res.data?.pagination?.nextCursor || null;
        const backendHasMore = !!res.data?.pagination?.hasMore;
        if (!backendHasMore || !nextCursor || nextCursor === cursor) {
          cursor = null;
          break;
        }
        cursor = nextCursor;
      }

      if (cursor) {
        console.warn(
          "selectAllCommentsIncludingPaginated: hit MAX_PAGES safety limit"
        );
      }

      setComments(aggregated);
      setHasMoreComments(!!cursor);
      setCommentCursor(cursor);
      setSelectedComments(aggregated.map((c) => c.id));
    } catch (err: any) {
      if (
        err?.name === "CanceledError" ||
        err?.name === "AbortError" ||
        err?.code === "ERR_CANCELED"
      ) {
        return;
      }
      console.error("Error loading all comments:", err);
      alert("Failed to load all comments. Please try again.");
    } finally {
      if (commentsAbortRef.current === abort) {
        setIsLoading(false);
      }
    }
  };

  const createRule = async () => {
    // Enhanced validation
    if (!newRule.name.trim()) {
      alert("Please enter a rule name");
      return;
    }
    if (
      newRule.triggerType === "ai_intent"
        ? !newRule.aiIntentDescription.trim()
        : !newRule.triggerValue.trim() && !newRule.template.trim()
    ) {
      alert(
        newRule.triggerType === "ai_intent"
          ? "Describe what this custom intent should detect"
          : "Please enter either a trigger value or select a template"
      );
      return;
    }
    if (newRule.scope === "post" && !newRule.postId) {
      alert("Please select a post for post-specific rules");
      return;
    }
    // Validate actions
    if (!newRule.actions || newRule.actions.length === 0) {
      alert("Please add at least one action");
      return;
    }
    for (const action of newRule.actions) {
      if (!action.type) {
        alert("All actions must have a type");
        return;
      }
      if (
        (action.type === "reply" || action.type === "dm") &&
        action.messageType === "static" &&
        (!action.autoReply || action.autoReply.trim() === "")
      ) {
        alert(
          "Reply and DM actions with static message type must include a message"
        );
        return;
      }
    }

    console.log("✅ Pre-validation newRule:", newRule);

    try {
      setIsSavingRule(true);

      const rule = {
        name: newRule.name,
        scope: newRule.scope,
        postId: newRule.scope === "post" ? newRule.postId : undefined,
        trigger: {
          type: newRule.triggerType,
          value: newRule.triggerValue || "",
          template: newRule.template || "",
          ...(newRule.triggerType === "ai_intent"
            ? {
                aiIntent: {
                  description: newRule.aiIntentDescription.trim(),
                  examples: newRule.aiIntentExamples
                    .map((e) => e.trim())
                    .filter(Boolean),
                  counterExamples: newRule.aiIntentCounterExamples
                    .map((e) => e.trim())
                    .filter(Boolean),
                },
              }
            : {}),
        },
        actions: newRule.actions.map((action) => ({
          type: action.type,
          autoReply: action.autoReply || "",
          messageType: action.messageType || "static",
        })),
        language: newRule.language,
        isActive: newRule.isActive,
        // useAI is auto-determined by backend based on trigger type
      };

      console.log("🚀 Creating rule with data:", rule);
      const response = await api.post(
        API_ENDPOINTS.MODERATION_RULES(pageId!),
        rule
      );

      // Refresh rules from server to ensure consistency
      await loadData();

      console.log("✅ Rule created successfully:", response.data);
      alert("Rule created successfully!");

      // Reset form
      resetRuleForm();
      setShowSidePanel(false);
    } catch (err: any) {
      console.error("Error creating rule:", err);
      alert(
        "Failed to create rule: " + (err.response?.data?.error || err.message)
      );
    } finally {
      setIsSavingRule(false);
    }
  };

  const resetRuleForm = () => {
    setNewRule({
      name: "",
      scope: "global",
      postId: "",
      triggerType: "contains",
      triggerValue: "",
      template: "",
      aiIntentDescription: "",
      aiIntentExamples: [],
      aiIntentCounterExamples: [],
      actions: [],
      language: "auto",
      useAI: false,
      isActive: true,
    });
    setEditingRule(null);
  };

  const handleAddRule = () => {
    resetRuleForm();

    // Pre-select the current post if one is selected from sidebar
    if (selectedSection !== "global" && selectedSection !== "all") {
      const currentPost = sidebarPosts.find(
        (post) => post.id === selectedSection
      );
      if (currentPost) {
        setNewRule((prev) => ({
          ...prev,
          scope: "post",
          postId: selectedSection,
        }));
      }
    }

    // Load rule posts initially
    loadRulePostsOnly(true);

    setShowSidePanel(true);
  };

  const handleCloseSidePanel = () => {
    setShowSidePanel(false);
    setEditingRule(null);
    resetRuleForm();
  };

  const handlePostSelectionFromSidebar = (postId: string) => {
    // If rule creation panel is open, update the rule form
    if (showSidePanel && newRule.scope === "post") {
      setNewRule((prev) => ({ ...prev, postId }));
    }
  };

  // Add CSS for line-clamp utility
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .line-clamp-2 {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
    `;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  // Sync post-filter state to the global sidebar (rendered by Layout)
  useEffect(() => {
    setFilterState({
      isActive: true,
      posts: sidebarPosts
        .filter((p) => p)
        .map((p) => ({
          id: p.id,
          message: p.message || "",
          imageUrl: getPostImage(p),
          createdTime: p.created_time || null,
          permalinkUrl: p.permalink_url || null,
        })),
      selectedId: selectedSection === "global" ? null : selectedSection,
      searchQuery: sidebarPostSearchQuery,
      loadingMore: loadingMoreSidebarPosts,
      hasMore:
        hasMoreSidebarPosts &&
        !!sidebarPostPagination?.nextCursor &&
        sidebarPosts.length <
          (sidebarPostPagination?.filteredCount || Infinity),
      handlers: {
        onSelectPost: (postId: string) => {
          setSelectedSection(postId);
          handlePostSelectionFromSidebar(postId);
        },
        onSelectGlobal: () => {
          setSelectedSection("global");
          if (showSidePanel && newRule.scope === "global") {
            setNewRule((prev) => ({ ...prev, postId: "" }));
          }
        },
        onSearchChange: (value: string) => {
          setSidebarPostSearchQuery(value);
          debouncedSidebarPostSearchRef.current?.(value);
        },
        onLoadMore: () => {
          loadSidebarPostsOnly(false);
        },
      },
    });
  }, [
    sidebarPosts,
    selectedSection,
    sidebarPostSearchQuery,
    loadingMoreSidebarPosts,
    hasMoreSidebarPosts,
    sidebarPostPagination,
    showSidePanel,
    newRule.scope,
    setFilterState,
  ]);

  useEffect(() => {
    return () => {
      resetFilterState();
    };
  }, [resetFilterState]);

  if (isLoading && !page) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading moderation dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="h-8 w-8 mx-auto mb-4 text-red-600" />
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={() => window.location.reload()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen overflow-hidden bg-gray-50 flex flex-col">
      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 sm:p-6 lg:p-8 space-y-6 lg:space-y-8">
          {/* Back Button - Only show when post is selected */}
          <AnimatePresence mode="wait">
            {currentSelectedPost && (
              <motion.div
                key="back-button"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                  onClick={() => setSelectedSection("global")}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to All Posts
                </Button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Context-Aware Header with Animation */}
          <AnimatePresence mode="wait">
            {currentSelectedPost ? (
              <motion.div
                key={`post-header-${selectedSection}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
                className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg border-l-4 border-blue-600 shadow-sm p-6"
              >
                <div className="flex items-start gap-3">
                  {getPostImage(currentSelectedPost) ? (
                    <img
                      src={getPostImage(currentSelectedPost)!}
                      alt="Selected Post"
                      className="w-14 h-14 object-cover rounded-lg shadow-md flex-shrink-0"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  ) : (
                    <span className="text-3xl flex-shrink-0">📄</span>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-blue-700 mb-1">
                      Moderating comments for
                    </p>
                    <h2 className="font-semibold text-gray-900 text-base mb-1">
                      {currentSelectedPost?.message?.substring(0, 150) ||
                        "Untitled Post"}
                      {currentSelectedPost?.message?.length > 150 && "..."}
                    </h2>
                    <div className="flex items-center gap-3 text-sm text-gray-600 flex-wrap">
                      {currentSelectedPost?.created_time && (
                        <span>
                          {new Date(currentSelectedPost.created_time).toLocaleDateString(undefined, {
                            year: "numeric", month: "short", day: "numeric",
                          })}{" "}
                          ·{" "}
                          {new Date(currentSelectedPost.created_time).toLocaleTimeString(undefined, {
                            hour: "numeric", minute: "2-digit",
                          })}
                        </span>
                      )}
                      {currentSelectedPost?.permalink_url && (
                        <a
                          href={currentSelectedPost.permalink_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-0.5"
                        >
                          View on {platformName} ↗
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="global-header"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
              >
                <div className="flex items-center gap-3 mb-1">
                  {page?.picture ? (
                    <img
                      src={page.picture}
                      alt={page?.name || "account"}
                      className="w-11 h-11 rounded-full object-cover border flex-shrink-0"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                    />
                  ) : (
                    <div
                      className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold ${
                        isInstagram
                          ? "bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600"
                          : "bg-blue-600"
                      }`}
                    >
                      {(page?.name || "?").charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h1 className="text-xl font-bold text-gray-900 truncate">
                        {page?.name || "Your account"}
                      </h1>
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                          isInstagram ? "bg-pink-100 text-pink-700" : "bg-blue-100 text-blue-700"
                        }`}
                      >
                        {platformName}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 truncate">
                      {page?.username ? `@${page.username} · ` : ""}
                      {isInstagram ? "Instagram account" : "Facebook page"}
                    </p>
                  </div>
                </div>
                <p className="text-gray-500 text-sm mt-1">
                  All posts · moderating comments across your {isInstagram ? "account" : "page"}
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Rules Management Content - Only show on Rules page */}
          {view === "rules" && (
            <motion.div
              key={`rules-${selectedSection}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="space-y-8"
            >
              {/* Test Rules Button at Top */}
              <div className="flex justify-end items-center">
                <Button
                  onClick={() => setShowTestModal(true)}
                  variant="outline"
                  className="bg-green-50 text-green-700 border-green-300 hover:bg-green-100"
                >
                  <TestTube className="h-4 w-4 mr-2" />
                  Test Moderation Rules
                </Button>
              </div>

              {/* Common Rules Section - Only show on Global view */}
              {selectedSection === "global" && (
                <Card className="border border-gray-200 shadow-sm rounded-lg">
                  <CardHeader className="border-b border-gray-200 px-6 py-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                          <Zap className="h-5 w-5 text-blue-600" />
                          Common Rules
                        </CardTitle>
                        <CardDescription className="mt-1 text-sm text-gray-500">
                          Activate common moderation rules with one click
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {quickRules.map((quickRule) => {
                        const existingRule = rules.find(r => r.name === quickRule.name);
                        const isActive = existingRule?.isActive || false;
                        const currentAction = existingRule?.actions[0]?.type || quickRule.defaultAction;
                        
                        return (
                          <div
                            key={quickRule.id}
                            className={`p-4 rounded-lg border transition-all ${
                              isActive
                                ? 'border-gray-300 bg-gray-50'
                                : 'border-gray-200 bg-white hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-lg ${
                                  isActive ? 'bg-gray-100' : 'bg-gray-50'
                                }`}>
                                  {quickRule.icon}
                                </div>
                                <div>
                                  <h4 className="font-medium text-sm text-gray-900">
                                    {quickRule.name}
                                  </h4>
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    {quickRule.description}
                                  </p>
                                </div>
                              </div>
                              <Switch
                                checked={isActive}
                                disabled={pendingQuickRuleNames.has(quickRule.name)}
                                onCheckedChange={(checked) =>
                                  handleQuickRuleToggle(quickRule, checked, currentAction)
                                }
                              />
                            </div>
                            
                            {isActive && (
                              <div className="mt-3 pt-3 border-t border-gray-200">
                                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                  Action
                                </label>
                                <Select
                                  value={currentAction}
                                  disabled={pendingQuickRuleNames.has(quickRule.name)}
                                  onValueChange={(value) =>
                                    handleQuickRuleActionChange(quickRule, value)
                                  }
                                >
                                  <SelectTrigger className="h-8 text-sm">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="hide" className="text-sm">
                                      <div className="flex items-center gap-2">
                                        <EyeOff className="h-3 w-3 text-orange-500" />
                                        <span>Hide Comment</span>
                                      </div>
                                    </SelectItem>
                                    <SelectItem value="delete" className="text-sm">
                                      <div className="flex items-center gap-2">
                                        <Trash2 className="h-3 w-3 text-red-500" />
                                        <span>Delete Comment</span>
                                      </div>
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Rules Table */}
              <Card className="border border-gray-200 shadow-sm rounded-lg">
              <CardHeader className="border-b border-gray-200 px-6 py-5">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                      <Shield className="h-5 w-5 text-blue-600" />
                      Custom Rules
                    </CardTitle>
                    <CardDescription className="mt-1 text-sm text-gray-500">
                      {selectedSection === "global"
                        ? "Rules that apply across all posts on your page"
                        : `Rules for ${
                            sidebarPosts
                              .find((p) => p.id === selectedSection)
                              ?.message?.substring(0, 30) || "Unknown Post"
                          }`}
                    </CardDescription>
                    {selectedSection === "global" && (
                      <label className="mt-2 flex items-center gap-2 text-xs text-gray-600">
                        <input
                          type="checkbox"
                          checked={showPostRulesInGlobal}
                          onChange={(e) =>
                            setShowPostRulesInGlobal(e.target.checked)
                          }
                          className="rounded border-gray-300"
                        />
                        Show all rules (global + posts)
                      </label>
                    )}
                  </div>
                  <div className="flex items-center space-x-3">
                    <Button
                      onClick={handleAddRule}
                      className="flex items-center space-x-2"
                    >
                      <Plus className="h-4 w-4" />
                      <span>Add Rule</span>
                    </Button>
                  </div>
                </div>
                </CardHeader>
                <CardContent className="p-6">
                 {sectionRules.length === 0 ? (
                   /* Empty State */
                   <div className="text-center py-16 px-4">
                     <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                       <Shield className="h-8 w-8 text-gray-400" />
                     </div>
                     <h3 className="text-lg font-medium text-gray-900 mb-2">
                       No moderation rules yet
                     </h3>
                     <p className="text-sm text-gray-500 max-w-md mx-auto mb-6">
                       You don't have any moderation rules yet. Add your first rule to automatically hide or reply to unwanted comments.
                     </p>
                     <Button
                       onClick={handleAddRule}
                       className="flex items-center space-x-2"
                     >
                       <Plus className="h-4 w-4" />
                       <span>Add Your First Rule</span>
                     </Button>
                   </div>
                 ) : (
                   <div className="border rounded-lg">
                     <div className="grid grid-cols-6 gap-4 p-4 bg-gray-50 border-b font-medium">
                       <div>Name</div>
                       <div>Trigger</div>
                       <div>Action</div>
                       <div>Scope</div>
                       <div>Status</div>
                       <div className="text-right">Actions</div>
                    </div>
                    <div className="divide-y">
                      {sectionRules.map((rule) => (
                     <div
                       key={rule.id}
                       className={`grid grid-cols-6 gap-4 p-4 items-center transition-all duration-500 ${
                         highlightedRuleId === rule.id
                           ? 'bg-blue-100 border-l-4 border-l-blue-500 -ml-1'
                           : ''
                       }`}
                     >
                        <div>{rule.name || "Unnamed Rule"}</div>
                        <div className="flex items-center">
                          <Badge className="text-xs">
                            {rule.trigger.type === "ai_intent"
                              ? "custom intent"
                              : rule.trigger.type}
                          </Badge>
                          <span className="ml-2 text-sm truncate">
                            {rule.trigger.type === "ai_intent"
                              ? rule.trigger.aiIntent?.description || ""
                              : rule.trigger.type === "template"
                                ? rule.trigger.template || rule.trigger.value
                                : rule.trigger.value}
                          </span>
                        </div>
                        <div>
                          {rule.actions.map((action, i) => (
                            <Badge
                              key={i}
                              className={
                                action.type === "delete"
                                  ? "bg-red-100 text-red-800"
                                  : action.type === "hide"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : "bg-blue-100 text-blue-800"
                              }
                            >
                              {action.type}
                            </Badge>
                          ))}
                        </div>
                        <div>
                          <Badge>
                            {rule.scope === "global"
                              ? "Global"
                              : "Post-specific"}
                          </Badge>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className="text-sm text-gray-700 font-medium">
                            {rule.isActive ? "Active" : "Inactive"}
                          </span>
                          <Switch
                            checked={rule.isActive}
                            disabled={pendingRuleIds.has(rule.id)}
                            onCheckedChange={(checked) =>
                              toggleRuleStatus(rule.id, checked)
                            }
                          />
                        </div>
                        <div className="flex justify-end space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => editRule(rule)}
                            className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            title="Edit rule"
                          >
                            <Edit3 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteRule(rule.id)}
                            disabled={pendingRuleIds.has(rule.id)}
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Delete rule"
                          >
                            {pendingRuleIds.has(rule.id) ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    ))}
                   </div>
                 </div>
                 )}
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Comments Section - Only show on Comments page */}
          {view === "comments" && (
            <motion.div
              key={`comments-${selectedSection}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="space-y-8"
            >
              {pageId && (
                <ImpactStrip
                  pageId={pageId}
                  onCardClick={handleStatCardClick}
                  activeKey={activeImpactKey}
                />
              )}

              {/* Review Queue promotion — surfaced above the comment list */}
              {pageId && (
                <button
                  type="button"
                  onClick={() =>
                    navigate(`/accounts/${pageId}/review`)
                  }
                  className={`w-full flex items-center justify-between gap-3 rounded-lg border px-4 py-3 text-left transition-all hover:shadow-md ${
                    reviewCount > 0
                      ? "border-amber-300 bg-amber-50 hover:bg-amber-100"
                      : "border-gray-200 bg-white hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        reviewCount > 0 ? "bg-amber-100" : "bg-gray-100"
                      }`}
                    >
                      <Shield
                        className={`h-5 w-5 ${
                          reviewCount > 0 ? "text-amber-600" : "text-gray-500"
                        }`}
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900">
                          Review Queue
                        </span>
                        {reviewCount > 0 && (
                          <Badge className="bg-amber-500 text-white text-xs">
                            {reviewCount}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">
                        {reviewCount > 0
                          ? `${reviewCount} comment${
                              reviewCount === 1 ? "" : "s"
                            } hidden by AI awaiting your review`
                          : "No comments awaiting review"}
                      </p>
                    </div>
                  </div>
                  <ChevronDown className="h-5 w-5 text-gray-400 -rotate-90" />
                </button>
              )}

              {/* Comments Table */}
              <Card className="border border-gray-200 shadow-sm rounded-lg">
              <CardContent className="p-6">
                {/* Unified Filter Bar with Chips */}
                <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6 shadow-sm">
                  <div className="flex flex-col gap-4">
                    {/* Search Bar */}
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <Input
                        placeholder="Search comments..."
                        value={commentSearchQuery}
                        onChange={(e) => debouncedCommentSearch(e.target.value)}
                        className="pl-10 bg-gray-50 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>

                    {/* Filter Chips Row */}
                    <div className="flex flex-wrap items-center gap-2">
                      {/* Rules Filter Chip */}
                      <div className="flex items-center gap-2 w-full sm:w-auto">
                        <span className="hidden sm:inline text-xs font-medium text-gray-600">Filters:</span>
                        <div className="relative w-full sm:inline-block sm:min-w-[280px]">
                          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-blue-300 rounded-md hover:bg-blue-50 text-sm h-8">
                            <Shield className="h-3.5 w-3.5 text-blue-600 flex-shrink-0" />
                            <ReactSelect
                              isMulti
                              value={sectionRules
                                .filter((rule) =>
                                  selectedRuleFilters.includes(rule.id)
                                )
                                .map((rule) => ({
                                  value: rule.id,
                                  label: rule.name || "Unnamed Rule",
                                }))}
                              onChange={(options) => {
                                const ids = options?.map((o) => o.value) || [];
                                setSelectedRuleFilters(ids);
                              }}
                              options={sectionRules.map((rule) => ({
                                value: rule.id,
                                label: rule.name || "Unnamed Rule",
                              }))}
                              placeholder="Rules: All"
                              classNamePrefix="react-select"
                              styles={{
                                control: (base) => ({
                                  ...base,
                                  minHeight: '20px',
                                  border: 'none',
                                  boxShadow: 'none',
                                  background: 'transparent',
                                  minWidth: '220px',
                                }),
                                valueContainer: (base) => ({
                                  ...base,
                                  padding: '0',
                                  fontSize: '0.875rem',
                                }),
                                placeholder: (base) => ({
                                  ...base,
                                  fontSize: '0.875rem',
                                  color: '#4B5563',
                                }),
                                multiValue: (base) => ({
                                  ...base,
                                  fontSize: '0.75rem',
                                  backgroundColor: '#DBEAFE',
                                }),
                                multiValueLabel: (base) => ({
                                  ...base,
                                  color: '#1E40AF',
                                  fontSize: '0.75rem',
                                }),
                                multiValueRemove: (base) => ({
                                  ...base,
                                  color: '#1E40AF',
                                  ':hover': {
                                    backgroundColor: '#BFDBFE',
                                    color: '#1E3A8A',
                                  },
                                }),
                                indicatorsContainer: (base) => ({
                                  ...base,
                                  height: '20px',
                                }),
                                dropdownIndicator: (base) => ({
                                  ...base,
                                  padding: '0 4px',
                                }),
                                clearIndicator: (base) => ({
                                  ...base,
                                  padding: '0 4px',
                                }),
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Date Filter Chip */}
                      <Select
                        value={commentDateFilter}
                        onValueChange={(v) => {
                          setCommentDateFilter(v);
                        }}
                      >
                        <SelectTrigger className="h-8 px-3 bg-white border-green-300 hover:bg-green-50 text-sm flex-1 sm:flex-none sm:w-auto min-w-0 sm:min-w-[130px]">
                          <Calendar className="h-3.5 w-3.5 text-green-600 mr-1.5" />
                          <SelectValue>
                            {commentDateFilter === "all" && "Date: All time"}
                            {commentDateFilter === "yesterday" && "Date: Last Day"}
                            {commentDateFilter === "week" && "Date: Last Week"}
                            {commentDateFilter === "month" && "Date: Last Month"}
                            {commentDateFilter === "custom" && "Date: Custom"}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Time</SelectItem>
                          <SelectItem value="yesterday">Last Day</SelectItem>
                          <SelectItem value="week">Last Week</SelectItem>
                          <SelectItem value="month">Last Month</SelectItem>
                          <SelectItem value="custom">Custom Days</SelectItem>
                        </SelectContent>
                      </Select>

                      {/* Status Filter — single-select dropdown, styled to
                          match the Rules and Date dropdowns. */}
                      <Select
                        value={statusFilter}
                        onValueChange={(v) => setStatusFilter(v as StatusFilter)}
                      >
                        <SelectTrigger className="h-8 px-3 bg-white border-purple-300 hover:bg-purple-50 text-sm flex-1 sm:flex-none sm:w-auto min-w-0 sm:min-w-[150px]">
                          <Filter className="h-3.5 w-3.5 text-purple-600 mr-1.5" />
                          <SelectValue>
                            Status: {statusFilterLabel(statusFilter)}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_FILTER_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {/* Clear filters button — filters auto-apply on change. */}
                      <div className="ml-auto flex items-center gap-2">
                        {hasActiveCommentFilters() && (
                          <Button
                            onClick={handleClearCommentFilters}
                            variant="outline"
                            size="sm"
                            className="h-8 px-4 text-sm border-gray-300 hover:bg-gray-50"
                            title="Clear all filters"
                          >
                            <X className="h-3.5 w-3.5 mr-1.5" />
                            Clear
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Selection + sync toolbar — thin row (Gmail/Linear pattern).
                    Bulk actions live in the contextual bar below, shown only
                    once a selection exists. "Fetch from Facebook" removed:
                    realtime webhooks keep the list current. */}
                <div className="flex items-center gap-3 mb-4 px-1">
                  {/* Checkbox + selection-options dropdown */}
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      aria-label="Select all on this page"
                      title="Select all on this page"
                      className="h-4 w-4 cursor-pointer accent-blue-600"
                      checked={
                        filteredComments.length > 0 &&
                        selectedComments.length >= filteredComments.length
                      }
                      ref={(el) => {
                        if (el)
                          el.indeterminate =
                            selectedComments.length > 0 &&
                            selectedComments.length < filteredComments.length;
                      }}
                      onChange={selectAllComments}
                    />
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          title="Selection options"
                          className="p-0.5 text-gray-500 hover:text-gray-700"
                        >
                          <ChevronDown className="h-3.5 w-3.5" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-56">
                        <DropdownMenuItem onClick={selectAllComments}>
                          All on page ({filteredComments.length})
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={selectAllCommentsIncludingPaginated}
                        >
                          All in filter{hasMoreComments ? " (loads all)" : ""}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setSelectedComments([])}>
                          None
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => selectVisibleByStatus("pending")}
                        >
                          By status: pending
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => selectVisibleByStatus("hidden")}
                        >
                          By status: hidden
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => selectVisibleByStatus("approved")}
                        >
                          By status: approved
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {/* Refresh (from database; webhooks keep it live) */}
                  <button
                    type="button"
                    onClick={() => loadCommentsOnly(true, false)}
                    disabled={isLoading || isSyncing}
                    title="Refresh comments"
                    className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <RefreshCw
                      className={`h-4 w-4 ${
                        isLoading || isSyncing ? "animate-spin" : ""
                      }`}
                    />
                  </button>

                  {/* Sync status line */}
                  <span className="text-xs text-gray-500">
                    {filteredComments.length} comment
                    {filteredComments.length === 1 ? "" : "s"} shown
                    {lastLoadedAt ? ` · synced ${timeAgo(lastLoadedAt)}` : ""}
                  </span>
                </div>

                {/* Off-screen-activity banner — shown when socket-pushed
                    comments were dropped because they didn't match the
                    active filters. Click clears all filters. */}
                {filteredOutSocketCount > 0 && (
                  <button
                    type="button"
                    onClick={clearAllCommentFilters}
                    className="w-full text-left text-xs text-blue-700 hover:text-blue-800 hover:bg-blue-100 px-2.5 py-1 rounded border border-blue-200 bg-blue-50 transition-colors mb-2"
                  >
                    {filteredOutSocketCount} new comment
                    {filteredOutSocketCount === 1 ? "" : "s"} hidden by current filters · clear filters
                  </button>
                )}

                {/* Batched-activity banner — shown when realtime comments
                    were rolled up because the animation buffer was full or
                    the user was scrolled away. Click scrolls to top and
                    refetches so the missed items show up. */}
                {batchedCount > 0 && (
                  <button
                    type="button"
                    onClick={showBatchedComments}
                    className="w-full text-left text-xs text-emerald-700 hover:text-emerald-800 hover:bg-emerald-100 px-2.5 py-1 rounded border border-emerald-200 bg-emerald-50 transition-colors mb-2"
                  >
                    {batchedCount} nouveau{batchedCount === 1 ? "" : "x"}{" "}
                    commentaire{batchedCount === 1 ? "" : "s"} modéré
                    {batchedCount === 1 ? "" : "s"} automatiquement · afficher
                  </button>
                )}

                {/* Contextual bulk-action bar — appears only once a selection
                    exists (Gmail/Superhuman pattern). Sticky so it stays in
                    reach while scrolling the list. */}
                {selectedComments.length > 0 && (
                  <div className="sticky top-2 z-10 flex flex-wrap items-center justify-between gap-2 mb-4 px-3 sm:px-4 py-2.5 rounded-lg bg-blue-600 text-white shadow-md">
                    <div className="flex items-center gap-3 text-sm">
                      <span className="font-semibold">
                        {selectedComments.length} selected
                      </span>
                      {selectedComments.length < filteredComments.length ? (
                        <button
                          type="button"
                          onClick={selectAllComments}
                          className="underline underline-offset-2 text-blue-100 hover:text-white"
                        >
                          Select all {filteredComments.length}
                        </button>
                      ) : hasMoreComments ? (
                        <button
                          type="button"
                          onClick={selectAllCommentsIncludingPaginated}
                          disabled={isLoading}
                          className="underline underline-offset-2 text-blue-100 hover:text-white disabled:opacity-60"
                        >
                          Select all in filter
                        </button>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => handleBulkReply("reply")}
                        disabled={bulkRunning}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium bg-white/15 hover:bg-white/25 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {bulkRunning ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <MessageSquare className="h-4 w-4" />
                        )}
                        Reply
                      </button>
                      <button
                        type="button"
                        onClick={() => handleBulkAction("hide")}
                        disabled={bulkRunning}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium bg-white/15 hover:bg-white/25 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {bulkRunning ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <EyeOff className="h-4 w-4" />
                        )}
                        Hide
                      </button>
                      <button
                        type="button"
                        onClick={() => handleBulkAction("delete")}
                        disabled={bulkRunning}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium bg-white/15 hover:bg-white/25 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {bulkRunning ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                        Delete
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedComments([])}
                        disabled={bulkRunning}
                        title="Clear selection"
                        className="ml-1 p-1.5 rounded-md hover:bg-white/20 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Comments List */}
                <div className="space-y-4">
                  <style>{`
                    @keyframes mjArriveGlow {
                      0%   { box-shadow: 0 0 0 0 rgba(37,99,235,0); }
                      22%  { box-shadow: 0 0 0 4px rgba(37,99,235,.5), 0 10px 34px -6px rgba(37,99,235,.5); }
                      100% { box-shadow: 0 0 0 0 rgba(37,99,235,0); }
                    }
                    .mj-arrive-glow { animation: mjArriveGlow 1.3s ease-out 1; }
                    @keyframes mjCensorSweep {
                      0%   { transform: translateX(-110%); }
                      55%  { transform: translateX(0%); }
                      100% { transform: translateX(0%); }
                    }
                    .mj-censor {
                      position: absolute; inset: 0; z-index: 5;
                      display: flex; align-items: center; justify-content: center;
                      backdrop-filter: blur(3px); -webkit-backdrop-filter: blur(3px);
                      animation: mjCensorSweep .6s cubic-bezier(.4,0,.2,1) both;
                    }
                    .mj-censor-badge {
                      display: flex; align-items: center; gap: 8px;
                      color: #fff; font-weight: 700; font-size: 15px;
                      letter-spacing: .2px; text-shadow: 0 1px 2px rgba(0,0,0,.25);
                    }
                  `}</style>
                  {/* Render incoming comments first (with animations) */}
                  <AnimatePresence mode="popLayout">
                    {Array.from(incomingComments.values()).map((comment) => {
                      const hasApplied = hasAppliedRules(comment);
                      const wouldMatch = wouldMatchSelectedRules(comment);
                      const potentialMatches = getPotentialRuleMatches(comment);
                      const appliedRules = comment.moderationResult?.matchedRules || [];

                      // Determine background color based on animation state
                      let backgroundClass = "bg-white";
                      let borderClass = "border-gray-200";
                      
                      if (comment.isProcessing) {
                        backgroundClass = "bg-blue-50 animate-pulse";
                        borderClass = "border-blue-300";
                      } else if (comment.animationState === "delete") {
                        backgroundClass = "bg-red-100";
                        borderClass = "border-red-400";
                      } else if (comment.animationState === "hide") {
                        backgroundClass = "bg-orange-100";
                        borderClass = "border-orange-400";
                      } else if (comment.animationState === "approved") {
                        backgroundClass = "bg-green-50";
                        borderClass = "border-green-300";
                      } else if (comment.animationState === "removing") {
                        backgroundClass = "bg-gray-50 opacity-0";
                        borderClass = "border-gray-200";
                      }

                      const isRemoving = comment.animationState === "removing";
                      // Shield-wipe / censor sweep when Moujib hides or deletes.
                      // 'removing' keeps the overlay up while the row slides out;
                      // status tells us which action it was (color + icon).
                      const censorActive =
                        comment.animationState === "hide" ||
                        comment.animationState === "delete" ||
                        isRemoving;
                      const censorIsDelete = comment.status === "deleted";
                      // Glow pulse fires once on arrival (element is freshly mounted).
                      const arriving =
                        comment.isProcessing ||
                        comment.animationState === "entering";

                      return (
                        <motion.div
                          key={`incoming-${comment.id}`}
                          initial={{ opacity: 0, y: -32, scale: 0.96 }}
                          animate={{
                            opacity: isRemoving ? 0 : 1,
                            x: isRemoving ? 90 : 0,
                            y: 0,
                            scale: isRemoving ? 0.92 : 1,
                          }}
                          exit={{ opacity: 0, scale: 0.95, height: 0, marginBottom: 0 }}
                          transition={{
                            duration: 0.35,
                            type: "spring",
                            stiffness: 280,
                            damping: 24,
                          }}
                          className={`relative overflow-hidden p-4 rounded-lg border transition-all duration-500 ${backgroundClass} ${borderClass} ${
                            arriving ? "mj-arrive-glow" : ""
                          } ${
                            hasApplied
                              ? "ring-2 ring-blue-300"
                              : wouldMatch
                              ? "ring-2 ring-blue-500"
                              : ""
                          }`}
                        >
                          {censorActive && (
                            <div
                              className="mj-censor"
                              style={{
                                background: censorIsDelete
                                  ? "linear-gradient(90deg, rgba(220,38,38,.96), rgba(185,28,28,.96))"
                                  : "linear-gradient(90deg, rgba(249,115,22,.96), rgba(234,88,12,.96))",
                              }}
                            >
                              <div className="mj-censor-badge">
                                {censorIsDelete ? (
                                  <Trash2 className="h-5 w-5" />
                                ) : (
                                  <Shield className="h-5 w-5" />
                                )}
                                <span>
                                  {censorIsDelete
                                    ? "Deleted by Moujib"
                                    : "Hidden by Moujib"}
                                </span>
                              </div>
                            </div>
                          )}
                          {/* Top Row */}
                          <div className="flex justify-between items-center mb-2">
                            <div className="flex items-center gap-2">
                              {comment.isProcessing && (
                                <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                              )}
                              <CommentAvatar info={getCommentUserInfo(comment)} />
                              <span className="font-medium">
                                {getCommentUserInfo(comment).name}
                              </span>
                              {comment.postLink && comment.postLink !== "#" && (
                                <a
                                  href={comment.postLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-0.5"
                                  title={`View comment on ${platformName}`}
                                >
                                  View on {platformName}
                                  <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </a>
                              )}
                              <span
                                className="text-xs text-gray-500"
                                title={fullTimestamp(comment.created_time)}
                              >
                                {timeAgo(comment.created_time)}
                              </span>
                              {comment.isNew && (
                                <Badge className="bg-blue-500 text-white text-xs">
                                  NEW
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {comment.isProcessing ? (
                                <Badge className="border bg-blue-100 text-blue-800 border-blue-400 text-xs font-semibold">
                                  ⏳ Processing...
                                </Badge>
                              ) : (
                                <Badge
                                  className={`border capitalize text-xs font-semibold shadow-sm ${
                                    comment.status === "deleted" || comment.moderationResult?.action === "delete"
                                      ? "bg-red-100 text-red-800 border-red-400"
                                      : comment.status === "hidden" || comment.moderationResult?.action === "hide"
                                      ? "bg-orange-100 text-orange-800 border-orange-400"
                                      : "bg-green-100 text-green-800 border-green-400"
                                  }`}
                                >
                                  {(comment.status === "deleted" || comment.moderationResult?.action === "delete") && "🗑️ "}
                                  {(comment.status === "hidden" || comment.moderationResult?.action === "hide") && "👁️‍🗨️ "}
                                  {(comment.status === "approved" || comment.moderationResult?.action === "approved") && "✓ "}
                                  {comment.status || comment.moderationResult?.action || "approved"}
                                </Badge>
                              )}
                            </div>
                          </div>

                          {/* Comment Message (+ media for image/sticker/GIF comments) */}
                          <div className="bg-white p-3 rounded border text-sm mb-3">
                            {comment.message ? (
                              <p className="whitespace-pre-wrap break-words">
                                {comment.message}
                              </p>
                            ) : !comment.attachment?.imageSrc ? (
                              <p className="text-gray-400 italic">No message content</p>
                            ) : null}
                            {comment.attachment?.imageSrc && (
                              <a
                                href={comment.attachment.url || comment.attachment.imageSrc}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={comment.message ? "mt-2 inline-block" : "inline-block"}
                              >
                                <img
                                  src={comment.attachment.imageSrc}
                                  alt={comment.attachment.title || comment.attachment.type || "comment media"}
                                  className="max-h-40 rounded border border-gray-200"
                                  loading="lazy"
                                />
                              </a>
                            )}
                          </div>

                          {/* Bot reply (nested under the original comment) */}
                          {comment.autoReply?.message && (
                            <div className="ml-6 mb-3 pl-3 border-l-2 border-blue-200">
                              <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                                <div className="flex items-center gap-2 mb-1 text-[11px] text-blue-700 font-semibold uppercase tracking-wide">
                                  {comment.autoReply.type === "private" ? (
                                    <Mail className="w-3 h-3" />
                                  ) : (
                                    <CornerDownRight className="w-3 h-3" />
                                  )}
                                  <span>
                                    {comment.autoReply.type === "private"
                                      ? "Auto-DM sent"
                                      : "Auto-reply posted"}
                                  </span>
                                  {comment.autoReply.sentAt && (
                                    <span className="text-blue-500/70 font-normal normal-case">
                                      · <span title={fullTimestamp(comment.autoReply.sentAt)}>{timeAgo(comment.autoReply.sentAt)}</span>
                                    </span>
                                  )}
                                  {comment.autoReply.successful === false && (
                                    <Badge className="bg-red-100 text-red-700 border border-red-200 text-[10px] py-0 px-1.5">
                                      failed
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-gray-800 whitespace-pre-wrap">
                                  {comment.autoReply.message}
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Matching Rules Flags */}
                          {potentialMatches.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {potentialMatches.map((rule) => {
                                const isApplied = appliedRules.includes(rule.id);
                                return (
                                  <span
                                    key={rule.id}
                                    className={`px-2 py-0.5 rounded-full text-xs font-medium border ${
                                      isApplied
                                        ? "bg-blue-100 text-blue-700 border-blue-300"
                                        : "bg-gray-100 text-gray-600 border-gray-300"
                                    }`}
                                  >
                                    <Shield className="h-3 w-3 inline-block mr-1" />
                                    {rule.name || rule.trigger.value}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>

                  {/* Render regular comments */}
                  {filteredComments.length > 0 ? (
                    filteredComments.map((comment) => {
                      const hasApplied = hasAppliedRules(comment);
                      const wouldMatch = wouldMatchSelectedRules(comment);
                      const potentialMatches = getPotentialRuleMatches(comment);
                      const appliedRules =
                        comment.moderationResult?.matchedRules || [];
                      const isFailed =
                        comment.status === "error" && !!comment.lastFailedAction;
                      // Visual state for moderated comments: dim + colored left
                      // border so hidden/deleted rows read as "acted on" at a glance.
                      const statusVisual =
                        comment.status === "deleted"
                          ? "opacity-50 border-l-4 border-l-red-400"
                          : comment.status === "hidden"
                          ? "opacity-75 border-l-4 border-l-amber-400"
                          : "";
                      const isReplying = replyingToId === comment.id;
                      const isPrivateReplying = privateReplyingToId === comment.id;

                      return (
                        <div
                          key={comment.id}
                          className={`p-4 rounded-lg border bg-white hover:shadow-sm transition ${
                            isFailed
                              ? "border-red-400"
                              : hasApplied
                              ? "border-blue-300"
                              : wouldMatch
                              ? "border-blue-500"
                              : "border-gray-200"
                          } ${statusVisual}`}
                        >
                          {/* Top Row */}
                          <div className="flex flex-wrap justify-between items-center gap-y-2 mb-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <input
                                type="checkbox"
                                checked={selectedComments.includes(comment.id)}
                                onChange={() =>
                                  toggleCommentSelection(comment.id)
                                }
                              />
                              <CommentAvatar info={getCommentUserInfo(comment)} />
                              <span className="font-medium truncate max-w-[40vw] sm:max-w-none">
                                {getCommentUserInfo(comment).name}
                              </span>
                              {comment.postLink && comment.postLink !== "#" && (
                                <a
                                  href={comment.postLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-0.5"
                                  title={`View comment on ${platformName}`}
                                >
                                  View on {platformName}
                                  <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </a>
                              )}
                              <span
                                className="text-xs text-gray-500"
                                title={fullTimestamp(comment.created_time)}
                              >
                                {timeAgo(comment.created_time)}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge
                                className={`border capitalize text-xs font-semibold shadow-sm ${
                                  comment.status === "error"
                                    ? "bg-red-100 text-red-800 border-red-400"
                                    : comment.status === "deleted"
                                    ? "bg-gray-200 text-gray-800 border-gray-400"
                                    : comment.status === "hidden"
                                    ? "bg-yellow-100 text-yellow-800 border-yellow-400"
                                    : comment.status === "approved"
                                    ? "bg-green-100 text-green-800 border-green-400"
                                    : "bg-blue-100 text-blue-800 border-blue-400" // pending
                                }`}
                              >
                                {comment.status === "error" && "⚠️ "}
                                {comment.status === "deleted" && "🗑️ "}
                                {comment.status === "hidden" && "👁️‍🗨️ "}
                                {comment.status === "approved" && "✓ "}
                                {comment.status === "pending" && "⏳ "}
                                {comment.status}
                              </Badge>

                              {/* Per-comment quick actions */}
                              {comment.status !== "deleted" && (
                                <div className="flex items-center gap-1">
                                  <button
                                    type="button"
                                    title="Reply"
                                    disabled={comment.isProcessing}
                                    onClick={() => {
                                      setReplyingToId(isReplying ? null : comment.id);
                                      setReplyDraft("");
                                    }}
                                    className={`p-1.5 rounded-md transition disabled:opacity-40 disabled:cursor-not-allowed ${
                                      isReplying
                                        ? "bg-blue-100 text-blue-700"
                                        : "text-gray-500 hover:bg-blue-50 hover:text-blue-600"
                                    }`}
                                  >
                                    <CornerDownRight className="h-4 w-4" />
                                  </button>
                                  {isInstagram && (
                                    <button
                                      type="button"
                                      title="Private Reply"
                                      disabled={comment.isProcessing}
                                      onClick={() => {
                                        setPrivateReplyingToId(
                                          isPrivateReplying ? null : comment.id
                                        );
                                        setPrivateReplyDraft("");
                                      }}
                                      className={`p-1.5 rounded-md transition disabled:opacity-40 disabled:cursor-not-allowed ${
                                        isPrivateReplying
                                          ? "bg-purple-100 text-purple-700"
                                          : "text-gray-500 hover:bg-purple-50 hover:text-purple-600"
                                      }`}
                                    >
                                      {comment.processingAction === "dm" ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <Mail className="h-4 w-4" />
                                      )}
                                    </button>
                                  )}
                                  {comment.status !== "hidden" && (
                                    <button
                                      type="button"
                                      title="Hide"
                                      disabled={comment.isProcessing}
                                      onClick={() =>
                                        handleCommentAction(comment.id, "hide")
                                      }
                                      className="p-1.5 rounded-md text-gray-500 hover:bg-amber-50 hover:text-amber-600 transition disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                      {comment.processingAction === "hide" ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <EyeOff className="h-4 w-4" />
                                      )}
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    title="Delete"
                                    disabled={comment.isProcessing}
                                    onClick={() => {
                                      if (
                                        window.confirm(
                                          `Delete this comment on ${platformName}? This cannot be undone.`
                                        )
                                      ) {
                                        handleCommentAction(comment.id, "delete");
                                      }
                                    }}
                                    className="p-1.5 rounded-md text-gray-500 hover:bg-red-50 hover:text-red-600 transition disabled:opacity-40 disabled:cursor-not-allowed"
                                  >
                                    {comment.processingAction === "delete" ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Trash2 className="h-4 w-4" />
                                    )}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Inline reply composer (Reply quick-action) */}
                          {isReplying && (
                            <div className="mb-3 flex items-start gap-2">
                              <Textarea
                                value={replyDraft}
                                onChange={(e) => setReplyDraft(e.target.value)}
                                placeholder="Write a public reply…"
                                rows={2}
                                className="flex-1 text-sm"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (
                                    (e.metaKey || e.ctrlKey) &&
                                    e.key === "Enter"
                                  ) {
                                    e.preventDefault();
                                    submitInlineReply(comment.id);
                                  }
                                }}
                              />
                              <div className="flex flex-col gap-1">
                                <Button
                                  size="sm"
                                  onClick={() => submitInlineReply(comment.id)}
                                  disabled={
                                    !replyDraft.trim() || comment.isProcessing
                                  }
                                  className="bg-blue-600 hover:bg-blue-700 text-white"
                                >
                                  {comment.processingAction === "reply" ? (
                                    <>
                                      <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                                      Sending…
                                    </>
                                  ) : (
                                    "Send"
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setReplyingToId(null);
                                    setReplyDraft("");
                                  }}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          )}

                          {/* Inline private-reply composer (Instagram private reply) */}
                          {isPrivateReplying && (
                            <div className="mb-3">
                              <div className="flex items-start gap-2">
                                <Textarea
                                  value={privateReplyDraft}
                                  onChange={(e) => setPrivateReplyDraft(e.target.value)}
                                  placeholder="Write a private reply…"
                                  rows={2}
                                  className="flex-1 text-sm"
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (
                                      (e.metaKey || e.ctrlKey) &&
                                      e.key === "Enter"
                                    ) {
                                      e.preventDefault();
                                      submitInlinePrivateReply(comment.id);
                                    }
                                  }}
                                />
                                <div className="flex flex-col gap-1">
                                  <Button
                                    size="sm"
                                    onClick={() => submitInlinePrivateReply(comment.id)}
                                    disabled={
                                      !privateReplyDraft.trim() || comment.isProcessing
                                    }
                                    className="bg-purple-600 hover:bg-purple-700 text-white"
                                  >
                                    {comment.processingAction === "dm" ? (
                                      <>
                                        <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                                        Sending…
                                      </>
                                    ) : (
                                      "Send"
                                    )}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setPrivateReplyingToId(null);
                                      setPrivateReplyDraft("");
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                              <p className="mt-1 text-[11px] text-gray-500">
                                Sent as a one-time private message to the commenter (within Instagram's 7-day messaging window).
                              </p>
                            </div>
                          )}

                          {/* Comment Message (+ media for image/sticker/GIF comments) */}
                          <div className="bg-white p-3 rounded border text-sm mb-3">
                            {comment.message ? (
                              <p className="whitespace-pre-wrap break-words">
                                {comment.message}
                              </p>
                            ) : !comment.attachment?.imageSrc ? (
                              <p className="text-gray-400 italic">No message content</p>
                            ) : null}
                            {comment.attachment?.imageSrc && (
                              <a
                                href={comment.attachment.url || comment.attachment.imageSrc}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={comment.message ? "mt-2 inline-block" : "inline-block"}
                              >
                                <img
                                  src={comment.attachment.imageSrc}
                                  alt={comment.attachment.title || comment.attachment.type || "comment media"}
                                  className="max-h-40 rounded border border-gray-200"
                                  loading="lazy"
                                />
                              </a>
                            )}
                          </div>

                          {/* Bot reply (nested under the original comment) */}
                          {comment.autoReply?.message && (
                            <div className="ml-6 mb-3 pl-3 border-l-2 border-blue-200">
                              <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                                <div className="flex items-center gap-2 mb-1 text-[11px] text-blue-700 font-semibold uppercase tracking-wide">
                                  {comment.autoReply.type === "private" ? (
                                    <Mail className="w-3 h-3" />
                                  ) : (
                                    <CornerDownRight className="w-3 h-3" />
                                  )}
                                  <span>
                                    {comment.autoReply.type === "private"
                                      ? "Auto-DM sent"
                                      : "Auto-reply posted"}
                                  </span>
                                  {comment.autoReply.sentAt && (
                                    <span className="text-blue-500/70 font-normal normal-case">
                                      · <span title={fullTimestamp(comment.autoReply.sentAt)}>{timeAgo(comment.autoReply.sentAt)}</span>
                                    </span>
                                  )}
                                  {comment.autoReply.successful === false && (
                                    <Badge className="bg-red-100 text-red-700 border border-red-200 text-[10px] py-0 px-1.5">
                                      failed
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-gray-800 whitespace-pre-wrap">
                                  {comment.autoReply.message}
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Failed-action banner with retry */}
                          {isFailed && (
                            <div className="mt-2 mb-3 flex items-start justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-3 py-2">
                              <div className="flex items-start gap-2 min-w-0">
                                <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                                <div className="text-xs text-red-700 break-words">
                                  {comment.lastError || getActionMessage(comment.lastFailedAction || "approve", "error")}
                                </div>
                              </div>
                              <Button
                                onClick={() => retryFailedAction(comment)}
                                size="sm"
                                variant="outline"
                                className="border-red-300 text-red-700 hover:bg-red-100 hover:text-red-800 shrink-0"
                              >
                                <RefreshCw className="h-3 w-3 mr-1" />
                                Action échouée — Réessayer
                              </Button>
                            </div>
                          )}

                          {/* Matching Rules Flags */}
                          {potentialMatches.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {potentialMatches.map((rule) => {
                                const isApplied = appliedRules.includes(
                                  rule.id
                                );
                                return (
                                  <span
                                    key={rule.id}
                                    className={`px-2 py-0.5 rounded-full text-xs font-medium border ${
                                      isApplied
                                        ? "bg-blue-100 text-blue-700 border-blue-300"
                                        : "bg-gray-100 text-gray-600 border-gray-300"
                                    }`}
                                  >
                                    <Shield className="h-3 w-3 inline-block mr-1" />
                                    {rule.name || rule.trigger.value}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-16 px-4">
                      <div className="flex justify-center mb-6">
                        <img 
                          src={commentsIllustration} 
                          alt="No comments" 
                          className="w-64 h-64 object-contain opacity-80"
                        />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-700 mb-3">
                        No comments yet
                      </h3>
                      <p className="text-gray-500 max-w-md mx-auto leading-relaxed mb-6">
                        {!isLoading && !isSyncing ? (
                          <>
                            No comments found in database. Click the button below to fetch comments from {platformName}.
                          </>
                        ) : (
                          <>
                            Comments will appear here once your page receives new interactions.
                          </>
                        )}
                      </p>
                      {!isLoading && !isSyncing && (
                        <Button
                          onClick={() => loadCommentsOnly(true, true)}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 gap-2"
                          disabled={isSyncing}
                        >
                          <Zap className="h-4 w-4" />
                          Fetch Comments from {platformName}
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Show More Comments Button */}
                  {hasMoreComments && (
                    <div className="text-center pt-4">
                      <Button
                        onClick={loadMoreComments}
                        disabled={loadingMore}
                        variant="outline"
                        className="border-gray-300 hover:bg-gray-50"
                      >
                        {loadingMore ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            Loading...
                          </>
                        ) : (
                          <>
                            <ChevronDown className="h-4 w-4 mr-2" />
                            Show More Comments
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
              </motion.div>
            )}

        </div>
      </div>

      {/* Create/Edit Rule Modal */}
      {showSidePanel && (
        <CreateRuleModal
          newRule={newRule}
          setNewRule={setNewRule}
          posts={rulePosts}
          triggerTemplates={triggerTemplates}
          onSave={editingRule ? updateRule : createRule}
          onCancel={handleCloseSidePanel}
          isSaving={isSavingRule || isEditingRule}
          isEditing={!!editingRule}
          hasMorePosts={hasMoreRulePosts}
          loadingMore={loadingMoreRulePosts}
          loadMorePosts={() => loadRulePostsOnly(false)}
          postPagination={rulePostPagination}
          postSearchValue={rulePostSearchQuery}
          setPostSearchValue={setRulePostSearchQuery}
          onSearchPosts={() => loadRulePostsOnly(true, rulePostSearchQuery)}
          isInstagram={isInstagram}
        />
      )}

      {/* Test Moderation Modal */}
      {showTestModal && (
        <TestModerationModal
          testComment={testComment}
          setTestComment={setTestComment}
          testResult={testResult}
          isTesting={isTesting}
          onTest={testModeration}
          onClose={() => {
            setShowTestModal(false);
            setTestComment("");
            setTestResult(null);
          }}
          isInstagram={isInstagram}
        />
      )}

      {/* Reply/DM Modal */}
      {showReplyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                {replyAction === "reply"
                  ? "Reply to Comment"
                  : "Send Private Message"}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowReplyModal(false)}
                className="h-6 w-6 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="space-y-4">
              {/* Message Type Selection - AI option hidden for Facebook review */}
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message Type
                </label>
                <div className="flex space-x-2">
                  <Button
                    variant={
                      replyMessageType === "static" ? "default" : "outline"
                    }
                    size="sm"
                    onClick={() => setReplyMessageType("static")}
                    className="flex-1"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Static Message
                  </Button>
                  <Button
                    variant={replyMessageType === "ai" ? "default" : "outline"}
                    size="sm"
                    onClick={() => generateAIReply(replyCommentId)}
                    disabled={isGeneratingAI}
                    className="flex-1"
                  >
                    <Bot className="h-4 w-4 mr-2" />
                    {isGeneratingAI ? "Generating..." : "AI Generated"}
                  </Button>
                </div>
              </div> */}

              {/* Message Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {replyAction === "reply"
                    ? "Reply Message"
                    : "Private Message"}
                </label>
                <textarea
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  placeholder={`Enter your ${
                    replyAction === "reply" ? "reply" : "private message"
                  }...`}
                  className="w-full h-32 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={isGeneratingAI}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setShowReplyModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    console.log(`🔍 [FRONTEND] Reply modal sending action:`, {
                      commentId: replyCommentId,
                      action: replyAction,
                      message: `"${replyMessage}"`,
                      messageType: replyMessageType,
                    });
                    handleCommentAction(
                      replyCommentId,
                      replyAction,
                      replyMessage,
                      replyMessageType
                    );
                    setShowReplyModal(false);
                  }}
                  disabled={!replyMessage.trim() || isGeneratingAI}
                  className="flex-1"
                >
                  {replyAction === "reply" ? "Send Reply" : "Send Message"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Multi-Step Rule Creator Component

const TestModerationModal: React.FC<{
  testComment: string;
  setTestComment: (comment: string) => void;
  testResult: any;
  isTesting: boolean;
  onTest: () => void;
  onClose: () => void;
  isInstagram: boolean;
}> = ({
  testComment,
  setTestComment,
  testResult,
  isTesting,
  onTest,
  onClose,
  isInstagram,
}) => {
  const platformName = isInstagram ? "Instagram" : "Facebook";
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <TestTube className="h-5 w-5 text-green-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              Test Moderation Rules
            </h2>
          </div>
          <Button onClick={onClose} variant="ghost" size="sm" className="h-8 w-8 p-0">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="p-6">
          {/* Facebook Post Mockup */}
          <div className="border border-gray-300 rounded-lg bg-white shadow-sm mb-4">
            {/* Post Header */}
            <div className="p-4 flex items-center space-x-3 border-b border-gray-200">
              <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold">
                P
              </div>
              <div>
                <div className="font-semibold text-sm text-gray-900">Your Page</div>
                <div className="text-xs text-gray-500">Just now · 🌐</div>
              </div>
            </div>

            {/* Post Content */}
            <div className="p-4 border-b border-gray-200">
              <p className="text-gray-700 text-sm">
                This is a sample {platformName} post to test how your moderation rules work.
              </p>
            </div>

            {/* Comments Section */}
            <div className="bg-gray-50">
              {/* Comment Input Area */}
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-start space-x-2">
                  <div className="w-8 h-8 rounded-full bg-gray-400 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                    U
                  </div>
                  <div className="flex-1">
                    <Textarea
                      placeholder="Write a test comment..."
                      value={testComment}
                      onChange={(e) => setTestComment(e.target.value)}
                      className="w-full min-h-[60px] text-sm rounded-lg border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-none"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          if (testComment.trim() && !isTesting) {
                            onTest();
                          }
                        }
                      }}
                    />
                    <div className="flex justify-end mt-2">
                      <Button
                        onClick={onTest}
                        disabled={isTesting || !testComment.trim()}
                        size="sm"
                        className="bg-blue-500 hover:bg-blue-600 text-white"
                      >
                        {isTesting ? (
                          <>
                            <Loader2 className="h-3 w-3 animate-spin mr-1" />
                            Testing...
                          </>
                        ) : (
                          'Post Comment'
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Test Result Display */}
              {testResult && (
                <div className="p-4">
                  <div
                    className={`p-4 rounded-lg border ${
                      testResult.action === "approve"
                        ? "bg-green-50 border-green-200"
                        : testResult.action === "hide"
                        ? "bg-yellow-50 border-yellow-200"
                        : testResult.action === "delete"
                        ? "bg-red-50 border-red-200"
                        : "bg-blue-50 border-blue-200"
                    }`}
                  >
              {/* Matched Rule (if any) */}
              {testResult.ruleName && (
                <div className="mb-3 pb-3 border-b border-gray-300">
                  <div className="flex items-center space-x-2 mb-1">
                    <Shield className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-semibold text-gray-700">
                      Matched Rule: {testResult.ruleName}
                    </span>
                  </div>
                  {testResult.templateMatched && (
                    <Badge className="bg-purple-50 text-purple-700 border-purple-200 text-xs">
                      Template Rule (Smart Classification)
                    </Badge>
                  )}
                </div>
              )}

              {/* Primary Action */}
              <div className="flex items-center space-x-2 mb-2">
                {testResult.action === "approve" ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : testResult.action === "hide" ? (
                  <EyeOff className="h-5 w-5 text-yellow-600" />
                ) : testResult.action === "delete" ? (
                  <Trash2 className="h-5 w-5 text-red-600" />
                ) : testResult.action === "reply" ? (
                  <MessageSquare className="h-5 w-5 text-blue-600" />
                ) : (
                  <Mail className="h-5 w-5 text-purple-600" />
                )}
                <span className="font-medium capitalize text-gray-900">
                  Primary Action: {testResult.action}
                </span>
                {/* Hidden AI badge for Facebook review */}
                {/* {testResult.isAI && (
                  <Badge className="bg-purple-50 text-purple-700 border-purple-200">
                    AI
                  </Badge>
                )} */}
              </div>

              {/* Reason */}
              <p className="text-sm text-gray-600 mb-2">{testResult.reason}</p>

              {/* All Actions (if multiple) */}
              {testResult.allActions && testResult.allActions.length > 1 && (
                <div className="mt-3 pt-3 border-t border-gray-300">
                  <p className="text-xs font-semibold text-gray-700 mb-2">
                    All Actions to be taken:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {testResult.allActions.map((action: any, index: number) => (
                      <Badge
                        key={index}
                        className={
                          action.type === "delete"
                            ? "bg-red-100 text-red-800"
                            : action.type === "hide"
                            ? "bg-yellow-100 text-yellow-800"
                            : action.type === "reply"
                            ? "bg-blue-100 text-blue-800"
                            : action.type === "dm"
                            ? "bg-purple-100 text-purple-800"
                            : "bg-green-100 text-green-800"
                        }
                      >
                        {action.type}
                        {action.autoReply && " (with message)"}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Confidence */}
              {testResult.confidence && (
                <p className="text-xs text-gray-500 mt-2">
                  Confidence: {Math.round(testResult.confidence * 100)}%
                </p>
              )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CreateRuleModal: React.FC<{
  newRule: NewRuleType;
  setNewRule: (
    rule: NewRuleType | ((prev: NewRuleType) => NewRuleType)
  ) => void;
  posts: FacebookPost[];
  triggerTemplates: any[];
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
  isEditing: boolean;
  hasMorePosts: boolean;
  loadingMore: boolean;
  loadMorePosts: () => void;
  postPagination: any;
  postSearchValue: string;
  setPostSearchValue: (v: string) => void;
  onSearchPosts: () => void;
  isInstagram: boolean;
}> = ({
  newRule,
  setNewRule,
  posts,
  triggerTemplates,
  onSave,
  onCancel,
  isSaving,
  isEditing,
  hasMorePosts,
  loadingMore,
  loadMorePosts,
  postPagination,
  postSearchValue,
  setPostSearchValue,
  onSearchPosts,
  isInstagram,
}) => {
  const [isPostDropdownOpen, setIsPostDropdownOpen] = useState(false);
  // Reveals the long tail of built-in intents beyond FEATURED_TEMPLATES.
  const [showAllTemplates, setShowAllTemplates] = useState(false);
  // Drafts being typed into the custom-intent builder's two example lists.
  const [intentExampleDraft, setIntentExampleDraft] = useState("");
  const [intentCounterDraft, setIntentCounterDraft] = useState("");

  // A custom intent is an AI condition, so it belongs to the "smart" tab. Left
  // out of this check it flipped the panel back to Exact Text the moment the
  // user clicked "Describe a custom intent".
  const conditionMode: "text" | "smart" =
    newRule.triggerType === "template" || newRule.triggerType === "ai_intent"
      ? "smart"
      : "text";

  const selectedPost = posts.find((p) => p.id === newRule.postId);

  const isSection1Valid =
    !!newRule.name.trim() &&
    (newRule.scope === "global" || !!newRule.postId);
  const [isSection1Collapsed, setIsSection1Collapsed] = useState<boolean>(
    () =>
      isEditing &&
      !!newRule.name.trim() &&
      (newRule.scope === "global" || !!newRule.postId)
  );

  // Map backend template values to display metadata (icon, colors, category).
  const templateMeta = (
    value: string
  ): {
    icon: any;
    color: string;
    bg: string;
    category: string;
  } => {
    const v = (value || "").toLowerCase();
    if (v.includes("profan") || v.includes("klem") || v.includes("offens") || v.includes("hate"))
      return { icon: Ban, color: "text-red-500", bg: "bg-red-100", category: "Content Quality" };
    if (v.includes("spam") || v.includes("promo"))
      return { icon: Megaphone, color: "text-orange-500", bg: "bg-orange-100", category: "Content Quality" };
    if (v.includes("price") || v.includes("cost") || v.includes("pricing"))
      return { icon: Search, color: "text-blue-500", bg: "bg-blue-100", category: "Business Intent" };
    if (v.includes("availability") || v.includes("stock"))
      return { icon: Search, color: "text-cyan-500", bg: "bg-cyan-100", category: "Business Intent" };
    if (v.includes("product_info") || v.includes("product"))
      return { icon: Search, color: "text-teal-500", bg: "bg-teal-100", category: "Business Intent" };
    if (v.includes("how_to_order") || v.includes("location") || v.includes("hours") || v.includes("contact"))
      return { icon: HelpCircle, color: "text-violet-500", bg: "bg-violet-100", category: "Business Intent" };
    if (v.includes("delivery") || v.includes("shipping") || v.includes("order"))
      return { icon: HelpCircle, color: "text-sky-500", bg: "bg-sky-100", category: "Business Intent" };
    if (v.includes("support") || v.includes("help") || v.includes("question"))
      return { icon: HelpCircle, color: "text-indigo-500", bg: "bg-indigo-100", category: "Business Intent" };
    if (v.includes("positive") || v.includes("praise") || v.includes("compliment"))
      return { icon: Smile, color: "text-green-500", bg: "bg-green-100", category: "Engagement Type" };
    if (v.includes("angry") || v.includes("complaint") || v.includes("negative") || v.includes("frustrat"))
      return { icon: Frown, color: "text-red-600", bg: "bg-red-100", category: "Engagement Type" };
    return { icon: Wand2, color: "text-gray-500", bg: "bg-gray-100", category: "Other" };
  };

  // Which built-in intents to render as tiles. Collapsed by default to the
  // featured shortlist, but a template already selected on this rule is always
  // included — otherwise editing an older rule would show an empty picker.
  const visibleTemplates: any[] = triggerTemplates
    .filter(
      (t: any) =>
        showAllTemplates ||
        FEATURED_TEMPLATES.includes(t.value) ||
        newRule.template === t.value
    )
    .map((t: any) => ({ ...t, meta: templateMeta(t.value) }));

  const hiddenTemplateCount = triggerTemplates.length - visibleTemplates.length;

  const groupedTemplates: Record<string, any[]> = visibleTemplates.reduce(
    (acc: Record<string, any[]>, t: any) => {
      (acc[t.meta.category] ||= []).push(t);
      return acc;
    },
    {}
  );

  // Business intent first, then the rest — see CATEGORY_ORDER.
  const templateCategories: [string, any[]][] = Object.entries(groupedTemplates)
    .sort(([a], [b]) => {
      const ia = CATEGORY_ORDER.indexOf(a);
      const ib = CATEGORY_ORDER.indexOf(b);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });


  const setConditionMode = (mode: "text" | "smart") => {
    if (mode === "text") {
      setNewRule((prev) => ({
        ...prev,
        triggerType: "contains",
        template: "",
      }));
    } else {
      // Re-entering smart mode must not wipe a custom intent the user already wrote.
      setNewRule((prev) => ({
        ...prev,
        triggerType: prev.triggerType === "ai_intent" ? "ai_intent" : "template",
      }));
    }
  };

  const handleSelectTemplate = (template: any) => {
    setNewRule((prev) => ({
      ...prev,
      triggerType: "template",
      template: template.value,
      triggerValue: template.trigger || template.value,
      // A rule has exactly one condition: choosing a template drops the custom one.
      aiIntentDescription: "",
      aiIntentExamples: [],
      aiIntentCounterExamples: [],
    }));
  };

  type IntentExampleField = "aiIntentExamples" | "aiIntentCounterExamples";

  /** Adds a drafted example to either list, de-duplicated and capped at 10. */
  const addIntentExample = (
    field: IntentExampleField,
    draft: string,
    clearDraft: () => void
  ) => {
    const value = draft.trim();
    if (!value) return;
    setNewRule((prev) => {
      const list = prev[field];
      if (
        list.length >= 10 ||
        list.some((e) => e.toLowerCase() === value.toLowerCase())
      ) {
        return prev;
      }
      return { ...prev, [field]: [...list, value] };
    });
    clearDraft();
  };

  const removeIntentExample = (field: IntentExampleField, index: number) => {
    setNewRule((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const handleUseCustomIntent = () => {
    setNewRule((prev) => ({
      ...prev,
      triggerType: "ai_intent",
      template: "",
      triggerValue: "",
    }));
  };

  // Canonical execution order — ensures replies post before the parent is hidden/deleted.
  const ACTION_ORDER: ("reply" | "dm" | "hide" | "delete")[] = [
    "reply",
    "dm",
    "hide",
    "delete",
  ];

  // delete is a terminal public action: no point hiding (redundant) or replying (orphaned).
  const isIncompatible = (
    a: "hide" | "delete" | "reply" | "dm",
    b: "hide" | "delete" | "reply" | "dm"
  ) => {
    if (a === b) return false;
    if (a === "delete" && (b === "hide" || b === "reply")) return true;
    if (b === "delete" && (a === "hide" || a === "reply")) return true;
    return false;
  };

  const toggleAction = (
    type: "hide" | "delete" | "reply" | "dm"
  ) => {
    const isSelected = newRule.actions.some((a) => a.type === type);
    const needsMessage = type === "reply" || type === "dm";

    setNewRule((prev) => {
      const next = isSelected
        ? prev.actions.filter((a) => a.type !== type)
        : [
            ...prev.actions.filter((a) => !isIncompatible(a.type as any, type)),
            {
              type,
              autoReply: needsMessage ? "" : undefined,
              messageType: needsMessage ? ("static" as const) : undefined,
            },
          ];

      // Re-sort by execution order so backend executes reply/dm before hide/delete.
      next.sort(
        (a, b) =>
          ACTION_ORDER.indexOf(a.type as any) -
          ACTION_ORDER.indexOf(b.type as any)
      );

      return { ...prev, actions: next };
    });
  };

  const updateActionMessage = (
    type: "reply" | "dm",
    message: string
  ) => {
    setNewRule((prev) => ({
      ...prev,
      actions: prev.actions.map((a) =>
        a.type === type
          ? { ...a, autoReply: message, messageType: a.messageType || "static" }
          : a
      ),
    }));
  };

  const actionMap: Record<string, { icon: any; label: string; color: string }> = {
    hide: { icon: EyeOff, label: "Hide Comment", color: "orange" },
    delete: { icon: Trash2, label: "Delete Comment", color: "red" },
    reply: { icon: CornerDownRight, label: "Auto-Reply", color: "blue" },
    dm: { icon: Mail, label: isInstagram ? "Private Reply" : "Send DM", color: "purple" },
  };

  const replyAction = newRule.actions.find((a) => a.type === "reply");
  const dmAction = newRule.actions.find((a) => a.type === "dm");

  const canSave =
    !!newRule.name.trim() &&
    (newRule.scope === "global" || !!newRule.postId) &&
    newRule.actions.length > 0 &&
    ((conditionMode === "text" && !!newRule.triggerValue.trim()) ||
      (conditionMode === "smart" &&
        (!!newRule.template ||
          (newRule.triggerType === "ai_intent" &&
            !!newRule.aiIntentDescription.trim()))));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-xl w-full max-w-4xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh] relative">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between shrink-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center shadow-sm">
              <Zap className="w-5 h-5 fill-white/20" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 leading-tight">
                {isEditing ? "Edit Automation Rule" : "Create Automation Rule"}
              </h2>
              <p className="text-sm text-gray-500">
                Define conditions to automatically moderate comments.
              </p>
            </div>
          </div>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto relative">
          {/* SECTION 1: Rule Info */}
          {isSection1Collapsed ? (
            <div className="bg-gray-50/50 px-8 py-3 border-b border-gray-200 flex items-center justify-between gap-4 animate-in fade-in duration-150">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="w-7 h-7 rounded-md bg-blue-600 text-white flex items-center justify-center shrink-0">
                  <CheckCircle className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex items-center gap-3">
                  <span className="font-semibold text-sm text-gray-900 truncate max-w-[260px]">
                    {newRule.name}
                  </span>
                  <span className="text-gray-300">•</span>
                  <div className="flex items-center gap-1.5 text-xs text-gray-600 shrink-0 min-w-0">
                    {newRule.scope === "global" ? (
                      <>
                        <Globe className="w-3.5 h-3.5 shrink-0" />
                        <span>All Posts</span>
                      </>
                    ) : (
                      <>
                        <FileText className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate max-w-[220px]">
                          {selectedPost?.message
                            ? selectedPost.message.length > 40
                              ? `${selectedPost.message.slice(0, 40)}…`
                              : selectedPost.message
                            : "Specific Post"}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setIsSection1Collapsed(false)}
                className="flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:bg-blue-50 px-3 py-1.5 rounded-md transition-colors shrink-0"
              >
                <Edit3 className="w-3.5 h-3.5" /> Edit
              </button>
            </div>
          ) : (
          <div className="bg-gray-50/50 px-8 py-6 border-b border-gray-200">
            {isSection1Valid && (
              <div className="flex justify-end mb-2">
                <button
                  onClick={() => setIsSection1Collapsed(true)}
                  className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 hover:text-gray-900 hover:bg-gray-100 px-3 py-1 rounded-md transition-colors"
                >
                  <ChevronDown className="w-3.5 h-3.5 rotate-180" /> Collapse
                </button>
              </div>
            )}
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-12 md:col-span-7 space-y-1.5">
                <label className="block text-sm font-semibold text-gray-700">
                  Rule Name
                </label>
                <input
                  type="text"
                  value={newRule.name}
                  onChange={(e) =>
                    setNewRule((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="e.g., Hide Competitor Mentions"
                  className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-shadow shadow-sm"
                />
              </div>

              {/* Target Post — one control instead of a scope toggle plus a
                  conditional picker. "All Posts" is just the first option, and
                  it is the default, so the common case needs no interaction. */}
              <div className="col-span-12 md:col-span-5 space-y-1.5">
                <label className="block text-sm font-semibold text-gray-700">
                  Target Post
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsPostDropdownOpen(!isPostDropdownOpen)}
                    className="w-full flex items-center justify-between gap-3 px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm shadow-sm hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-shadow"
                  >
                    {newRule.scope === "global" || !selectedPost ? (
                      <span className="flex items-center gap-2 font-medium text-gray-900">
                        <Globe className="w-4 h-4 text-blue-600 shrink-0" />
                        All Posts
                      </span>
                    ) : (
                      <span className="flex items-center gap-2 min-w-0">
                        {selectedPost.picture ? (
                          <img
                            src={selectedPost.picture}
                            alt=""
                            className="w-6 h-6 rounded object-cover shrink-0 border border-gray-200"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = "none";
                            }}
                          />
                        ) : (
                          <Megaphone className="w-4 h-4 text-gray-400 shrink-0" />
                        )}
                        <span className="truncate font-medium text-gray-900">
                          {selectedPost.message
                            ? selectedPost.message.length > 40
                              ? `${selectedPost.message.substring(0, 40)}...`
                              : selectedPost.message
                            : "Untitled Post"}
                        </span>
                      </span>
                    )}
                    <ChevronDown
                      className={`w-4 h-4 shrink-0 text-gray-400 transition-transform ${
                        isPostDropdownOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {isPostDropdownOpen && (
                    <>
                      {/* Click-away layer so the menu closes like a real select */}
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setIsPostDropdownOpen(false)}
                      />
                      <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl z-50 flex flex-col overflow-hidden animate-in slide-in-from-top-2 fade-in">
                        {/* "All Posts" is an option in the same list, not a
                            separate mode the user has to discover. */}
                        <button
                          type="button"
                          onClick={() => {
                            setNewRule((prev) => ({
                              ...prev,
                              scope: "global",
                              postId: "",
                            }));
                            setIsPostDropdownOpen(false);
                          }}
                          className={`w-full flex items-center gap-3 p-3 text-left border-b border-gray-100 hover:bg-blue-50 transition-colors ${
                            newRule.scope === "global" ? "bg-blue-50" : ""
                          }`}
                        >
                          <div className="w-10 h-10 rounded bg-blue-50 flex items-center justify-center shrink-0 border border-blue-100">
                            <Globe className="w-5 h-5 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900">
                              All Posts
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Apply this rule across the whole page
                            </p>
                          </div>
                          {newRule.scope === "global" && (
                            <Check className="w-4 h-4 text-blue-600 shrink-0" />
                          )}
                        </button>

                        <div className="p-2 border-b border-gray-100 bg-gray-50">
                          <div className="relative flex items-center gap-2">
                            <div className="relative flex-1">
                              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                              <input
                                autoFocus
                                type="text"
                                placeholder="Search through posts..."
                                className="w-full pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-md text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                                value={postSearchValue}
                                onChange={(e) => setPostSearchValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") onSearchPosts();
                                  else if (e.key === "Escape") {
                                    setPostSearchValue("");
                                    onSearchPosts();
                                  }
                                }}
                              />
                              {loadingMore && (
                                <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={onSearchPosts}
                              className="px-3 py-2 rounded-md bg-white border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors"
                              title="Search"
                              disabled={loadingMore}
                            >
                              {loadingMore ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Search className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </div>

                        <div className="max-h-60 overflow-y-auto">
                          {posts.length > 0 ? (
                            posts.map((post) => (
                              <button
                                key={post.id}
                                type="button"
                                onClick={() => {
                                  // Picking a post is what switches the scope —
                                  // the user never sets "scope" directly.
                                  setNewRule((prev) => ({
                                    ...prev,
                                    scope: "post",
                                    postId: post.id,
                                  }));
                                  setIsPostDropdownOpen(false);
                                }}
                                className={`w-full flex items-center gap-3 p-3 text-left hover:bg-blue-50 border-b border-gray-50 last:border-0 transition-colors ${
                                  newRule.scope === "post" && newRule.postId === post.id
                                    ? "bg-blue-50"
                                    : ""
                                }`}
                              >
                                {post.picture ? (
                                  <img
                                    src={post.picture}
                                    alt="Post"
                                    className="w-10 h-10 rounded object-cover shrink-0 border border-gray-200"
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).style.display = "none";
                                    }}
                                  />
                                ) : (
                                  <div className="w-10 h-10 rounded bg-gray-50 flex items-center justify-center shrink-0 border border-gray-200">
                                    <Megaphone className="w-5 h-5 text-gray-400" />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">
                                    {post.message || "Untitled Post"}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    {post.created_time
                                      ? new Date(post.created_time).toLocaleDateString()
                                      : ""}
                                  </p>
                                </div>
                                {newRule.scope === "post" && newRule.postId === post.id && (
                                  <Check className="w-4 h-4 text-blue-600 shrink-0" />
                                )}
                              </button>
                            ))
                          ) : (
                            <div className="p-8 text-center text-gray-500 text-sm">
                              {postSearchValue
                                ? `No posts match "${postSearchValue}"`
                                : "No posts available"}
                            </div>
                          )}

                          {hasMorePosts &&
                            postPagination?.nextCursor &&
                            posts.length > 0 && (
                              <div className="flex justify-center p-2 border-t border-gray-100">
                                <button
                                  type="button"
                                  onClick={loadMorePosts}
                                  disabled={loadingMore}
                                  className="text-xs font-medium text-blue-600 hover:bg-blue-50 px-3 py-1.5 rounded-md transition-colors disabled:opacity-50"
                                >
                                  {loadingMore ? (
                                    <>
                                      <Loader2 className="h-3 w-3 animate-spin mr-1 inline" />
                                      Loading...
                                    </>
                                  ) : (
                                    "Show more posts"
                                  )}
                                </button>
                              </div>
                            )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          )}

          {/* SECTION 2: IF comment... */}
          <div className="bg-white px-8 py-8 border-b border-gray-200">
            <div className="mb-4 flex items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  <span className="text-blue-600 font-extrabold mr-2">IF</span>
                  comment...
                </h3>
                <p className="text-sm text-gray-500">
                  Define the condition that must match.
                </p>
              </div>

              <div className="flex bg-gray-100 p-1 rounded-lg border border-gray-200">
                <button
                  onClick={() => setConditionMode("smart")}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-bold transition-all ${
                    conditionMode === "smart"
                      ? "bg-white text-blue-700 shadow-sm ring-1 ring-black/5"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <Wand2 className="w-4 h-4" /> AI Smart
                </button>
                <button
                  onClick={() => setConditionMode("text")}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-bold transition-all ${
                    conditionMode === "text"
                      ? "bg-white text-blue-700 shadow-sm ring-1 ring-black/5"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <Type className="w-4 h-4" /> Exact Text
                </button>
              </div>
            </div>

            <div className="relative rounded-xl border-2 border-gray-200 bg-white transition-all">
              {conditionMode === "text" && (
                <div className="p-6 flex items-start gap-5 animate-in fade-in zoom-in-95 duration-200">
                  <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center shrink-0 mt-1">
                    <Type className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1 pr-4">
                    <h4 className="text-sm font-bold text-gray-900 mb-3">
                      Text Match Configuration
                    </h4>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <select
                        value={newRule.triggerType}
                        onChange={(e) =>
                          setNewRule((prev) => ({
                            ...prev,
                            triggerType: e.target.value as
                              | "contains"
                              | "starts_with"
                              | "ends_with",
                          }))
                        }
                        className="w-full sm:w-48 bg-gray-50 border border-gray-200 text-sm font-medium rounded-lg px-3 py-2.5 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                      >
                        <option value="contains">Contains any of</option>
                        <option value="starts_with">Starts with</option>
                        <option value="ends_with">Ends with</option>
                      </select>
                      <input
                        type="text"
                        value={newRule.triggerValue}
                        onChange={(e) =>
                          setNewRule((prev) => ({
                            ...prev,
                            triggerValue: e.target.value,
                          }))
                        }
                        placeholder="e.g., free, discount, click here (comma separated)"
                        className="flex-1 bg-white border border-gray-300 text-sm rounded-lg px-4 py-2.5 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                      />
                    </div>
                  </div>
                </div>
              )}

              {conditionMode === "smart" && (
                <div className="p-5 space-y-4 animate-in fade-in zoom-in-95 duration-200">
                  {/* One mode at a time: picking "your own intent" hides the
                      built-in chips, and cancelling brings them straight back. */}
                  {newRule.triggerType !== "ai_intent" && (
                    <>
                    {templateCategories.map(([category, templates]) => (
                      <div key={category}>
                        <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5 px-0.5">
                          {category}
                        </h4>
                        {/* Compact chips, not cards — four of these should read at
                            a glance rather than fill the panel. */}
                        <div className="flex flex-wrap gap-2">
                          {templates.map((t: any) => {
                            const Icon = t.meta.icon;
                            const isSelected =
                              newRule.triggerType === "template" &&
                              newRule.template === t.value;
                            return (
                              <button
                                key={t.value}
                                type="button"
                                onClick={() => handleSelectTemplate(t)}
                                title={t.trigger || t.label}
                                className={`inline-flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full border text-sm transition-all ${
                                  isSelected
                                    ? "border-blue-500 bg-blue-50 text-blue-800 ring-1 ring-blue-500 font-semibold"
                                    : "border-gray-200 bg-white text-gray-700 hover:border-blue-400 hover:bg-blue-50/40 font-medium"
                                }`}
                              >
                                <span
                                  className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${t.meta.bg} ${t.meta.color}`}
                                >
                                  <Icon className="w-3 h-3" />
                                </span>
                                <span className="whitespace-nowrap">{t.label}</span>
                                {isSelected && (
                                  <Check className="w-3.5 h-3.5 shrink-0 text-blue-600" />
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}

                    {(hiddenTemplateCount > 0 || showAllTemplates) && (
                      <button
                        type="button"
                        onClick={() => setShowAllTemplates((v) => !v)}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-blue-700 transition-colors"
                      >
                        <ChevronDown
                          className={`w-3.5 h-3.5 transition-transform ${
                            showAllTemplates ? "rotate-180" : ""
                          }`}
                        />
                        {showAllTemplates
                          ? "Show fewer"
                          : `Show ${hiddenTemplateCount} more`}
                      </button>
                    )}
                    </>
                  )}

                  {/* ---- Custom intent ------------------------------------
                       Replaces the chips entirely while active, so the panel
                       shows one way of defining a condition at a time. The
                       "Flag a comment when…" framing makes the field
                       self-explanatory without extra helper copy. */}
                  {newRule.triggerType !== "ai_intent" ? (
                    <button
                      type="button"
                      onClick={handleUseCustomIntent}
                      className="group w-full flex items-center gap-2 pt-3 border-t border-dashed border-gray-200 text-left"
                    >
                      <Wand2 className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                      <span className="text-sm text-gray-500 group-hover:text-blue-700 transition-colors">
                        None of these?{" "}
                        <span className="font-semibold underline decoration-blue-300 underline-offset-2">
                          Describe your own
                        </span>
                      </span>
                    </button>
                  ) : (
                    <div className="space-y-3 animate-in fade-in duration-150">
                      <div className="flex items-center justify-between gap-3">
                        <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
                          <Wand2 className="w-3.5 h-3.5 text-blue-600" />
                          Your own intent
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            setNewRule((prev) => ({
                              ...prev,
                              triggerType: "template",
                              aiIntentDescription: "",
                              aiIntentExamples: [],
                              aiIntentCounterExamples: [],
                            }))
                          }
                          className="text-xs font-semibold text-gray-500 hover:text-gray-900"
                        >
                          Cancel
                        </button>
                      </div>

                      <div>
                        <label
                          htmlFor="custom-intent-sentence"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Flag a comment when…
                        </label>
                        <input
                          id="custom-intent-sentence"
                          type="text"
                          autoFocus
                          value={newRule.aiIntentDescription}
                          onChange={(e) =>
                            setNewRule((prev) => ({
                              ...prev,
                              aiIntentDescription: e.target.value,
                            }))
                          }
                          placeholder="the customer asks about wholesale pricing"
                          className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                        />
                        <p className="mt-1.5 text-xs text-gray-500">
                          Describe the meaning, not keywords.
                          {!newRule.aiIntentDescription.trim() && (
                            <>
                              {" Try: "}
                              {INTENT_STARTERS.map((starter, i) => (
                                <React.Fragment key={starter.label}>
                                  {i > 0 && " · "}
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setNewRule((prev) => ({
                                        ...prev,
                                        aiIntentDescription: starter.text,
                                      }))
                                    }
                                    className="text-blue-700 font-medium hover:underline"
                                  >
                                    {starter.label}
                                  </button>
                                </React.Fragment>
                              ))}
                            </>
                          )}
                        </p>
                      </div>

                      {(
                        [
                          {
                            field: "aiIntentExamples" as IntentExampleField,
                            id: "custom-intent-example",
                            label: "Comments that should match",
                            hint: "improves accuracy",
                            placeholder: "Paste a real comment, then press Enter",
                            draft: intentExampleDraft,
                            setDraft: setIntentExampleDraft,
                          },
                          {
                            field: "aiIntentCounterExamples" as IntentExampleField,
                            id: "custom-intent-counter-example",
                            label: "Comments that should NOT match",
                            hint: "stops false alarms",
                            placeholder: "Paste a comment it should ignore",
                            draft: intentCounterDraft,
                            setDraft: setIntentCounterDraft,
                          },
                        ] as const
                      ).map((cfg) => {
                        const items = newRule[cfg.field];
                        return (
                          <div key={cfg.field}>
                            <label
                              htmlFor={cfg.id}
                              className="block text-sm font-medium text-gray-700 mb-1"
                            >
                              {cfg.label}{" "}
                              <span className="font-normal text-gray-400">
                                — optional, {cfg.hint}
                              </span>
                            </label>

                            {items.length > 0 && (
                              <div className="space-y-1 mb-1.5">
                                {items.map((ex, i) => (
                                  <div
                                    key={`${ex}-${i}`}
                                    className="flex items-center gap-2 bg-white rounded-md border border-gray-200 px-2.5 py-1.5"
                                  >
                                    <span className="flex-1 min-w-0 text-sm text-gray-800 break-words">
                                      {ex}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        removeIntentExample(cfg.field, i)
                                      }
                                      className="text-gray-400 hover:text-red-600 shrink-0"
                                      aria-label={`Remove example: ${ex}`}
                                    >
                                      <X className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}

                            {items.length < 10 && (
                              <input
                                id={cfg.id}
                                type="text"
                                value={cfg.draft}
                                onChange={(e) => cfg.setDraft(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    addIntentExample(cfg.field, cfg.draft, () =>
                                      cfg.setDraft("")
                                    );
                                  }
                                }}
                                onBlur={() =>
                                  addIntentExample(cfg.field, cfg.draft, () =>
                                    cfg.setDraft("")
                                  )
                                }
                                placeholder={cfg.placeholder}
                                className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

          </div>

          {/* SECTION 3: DO... */}
          <div className="bg-gray-50/50 px-8 py-5">
            <div className="mb-3">
              <h3 className="text-lg font-bold text-gray-900">
                <span className="text-blue-600 font-extrabold mr-2">DO</span>
                these actions...
              </h3>
              <p className="text-xs text-gray-500">
                Pick one or more actions. They'll run in this order: reply →{" "}
                {isInstagram ? "private reply" : "DM"} → hide → delete.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
              {(["hide", "delete", "reply", "dm"] as const).map((key) => {
                const a = actionMap[key];
                const Icon = a.icon;
                const isSelected = newRule.actions.some((x) => x.type === key);
                const blockingAction = newRule.actions.find(
                  (x) => !isSelected && isIncompatible(x.type as any, key)
                );
                const isDisabled = !!blockingAction;
                const disabledReason = blockingAction
                  ? key === "delete"
                    ? "Delete can't combine with hide or reply — it removes the comment entirely."
                    : `Delete is selected — ${a.label.toLowerCase()} would have nothing to attach to.`
                  : undefined;
                return (
                  <button
                    key={key}
                    onClick={() => !isDisabled && toggleAction(key)}
                    disabled={isDisabled}
                    title={disabledReason}
                    className={`relative flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border-2 transition-all ${
                      isDisabled
                        ? "border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed"
                        : isSelected
                        ? a.color === "orange"
                          ? "border-orange-500 bg-white shadow-sm ring-1 ring-orange-500"
                          : a.color === "red"
                          ? "border-red-500 bg-white shadow-sm ring-1 ring-red-500"
                          : a.color === "purple"
                          ? "border-purple-500 bg-white shadow-sm ring-1 ring-purple-500"
                          : "border-blue-500 bg-white shadow-sm ring-1 ring-blue-500"
                        : "border-gray-200 hover:border-gray-300 bg-white"
                    }`}
                  >
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                        isSelected
                          ? a.color === "orange"
                            ? "bg-orange-100 text-orange-600"
                            : a.color === "red"
                            ? "bg-red-100 text-red-600"
                            : a.color === "purple"
                            ? "bg-purple-100 text-purple-600"
                            : "bg-blue-100 text-blue-600"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <span
                      className={`font-semibold text-sm ${
                        isSelected
                          ? a.color === "orange"
                            ? "text-orange-900"
                            : a.color === "red"
                            ? "text-red-900"
                            : a.color === "purple"
                            ? "text-purple-900"
                            : "text-blue-900"
                          : "text-gray-700"
                      }`}
                    >
                      {a.label}
                    </span>
                    {isSelected && (
                      <CheckCircle className="absolute top-1.5 right-1.5 w-3.5 h-3.5 text-current opacity-60" />
                    )}
                  </button>
                );
              })}
            </div>

            {newRule.actions.length > 0 && (
              <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-100 rounded-md text-xs text-blue-900 flex items-start gap-2">
                <CheckCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>
                  When this rule matches:{" "}
                  <span className="font-semibold">
                    {newRule.actions
                      .map((a, i) => {
                        const phrase = {
                          reply: "post a public reply",
                          dm: isInstagram ? "send a private reply to the commenter" : "send a DM to the commenter",
                          hide: "hide the original comment",
                          delete: "delete the comment",
                        }[a.type as "reply" | "dm" | "hide" | "delete"];
                        if (i === 0)
                          return phrase!.charAt(0).toUpperCase() + phrase!.slice(1);
                        if (i === newRule.actions.length - 1)
                          return `then ${phrase}`;
                        return phrase;
                      })
                      .join(", ")}
                    .
                  </span>
                </span>
              </div>
            )}

            {replyAction && (
              <div className="animate-in slide-in-from-top-2 fade-in">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Reply Message
                </label>
                <textarea
                  value={replyAction.autoReply || ""}
                  onChange={(e) => updateActionMessage("reply", e.target.value)}
                  placeholder="Type the exact message the bot should reply with..."
                  rows={2}
                  className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 shadow-sm resize-none"
                />
              </div>
            )}

            {dmAction && (
              <div className="animate-in slide-in-from-top-2 fade-in">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  DM Message
                </label>
                <textarea
                  value={dmAction.autoReply || ""}
                  onChange={(e) => updateActionMessage("dm", e.target.value)}
                  placeholder="Type the direct message to send the commenter..."
                  rows={2}
                  className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 shadow-sm resize-none"
                />
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 py-4 border-t border-gray-200 bg-white flex items-center justify-between shrink-0 z-10 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
          <button
            onClick={onCancel}
            className="px-5 py-2.5 text-sm font-semibold text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={!canSave || isSaving}
            className="flex items-center px-8 py-2.5 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600 disabled:shadow-md"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {isEditing ? "Update Rule" : "Save Rule"}
              </>
            )}
          </button>
        </div>

      </div>
    </div>
  );
};

export default ModerationDashboard;
